'use client'

import React from 'react'
import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import { useAnalysisStore, type Trigger } from '../lib/store'
import { saveAnalysisData } from '../lib/dataUtils'
import { 
  // deleteAnalysisData, 
  // checkApiHealth,
  // fetchAllImages,
  createUserInfo,
  getUserInfo,
  userLogin,
  type UserInfoRequest,
  type UserInfo,
  type UserLoginResponse,
  deleteTrigger,
  type TriggerDelete,
  updateTrigger,
  type TriggerUpdate,
  updateImageInfoBefore,
  generateSuggestion,
  type SuggestionResponse,
  updateImageInfoAfter,
  deleteImage,
  uploadImage,
  fetchImage,
  createCustomTrigger,
  type Image,
  createUserGad7,
  createUserContact,
  reportSuggestion,
  rateSuggestion
} from '../api/imageApi'
import { useI18n } from '../components/I18nProvider'

const TEST_EXPERIENCE_CODE = '50ffc67d-99c5-4519-9351-a89611cc75e7';

const SAMPLE_IMAGES = [
  { src: '/images/RedNote_test_pic1.jpg', labelKey: 'redNotePic1' },
  { src: '/images/RedNote_test_pic2.jpg', labelKey: 'redNotePic2' },
  { src: '/images/TikTok_test_pic1.jpeg', labelKey: 'tikTokPic1' },
  { src: '/images/TikTok_test_pic2.jpeg', labelKey: 'tikTokPic2' },
] as const;

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 30;

type ImageUrlBaseline = {
  initialS3Location?: string;
  initialUrl?: string;
};

const normalizeS3Key = (location: string): string => {
  if (!location) return '';

  if (location.startsWith('s3://')) {
    const parts = location.replace('s3://', '').split('/');
    parts.shift();
    return parts.join('/');
  }

  try {
    const parsed = new URL(location);
    return decodeURIComponent(parsed.pathname.replace(/^\//, ''));
  } catch {
    return location.replace(/^\//, '');
  }
};

const presignedUrlMatchesS3Location = (url: string, s3Location: string): boolean => {
  if (!url || !s3Location) return false;

  const key = normalizeS3Key(s3Location);
  if (!key) return false;

  try {
    const decodedUrl = decodeURIComponent(url);
    const fileName = key.split('/').pop();
    return decodedUrl.includes(key) || Boolean(fileName && decodedUrl.includes(fileName));
  } catch {
    return url.includes(key);
  }
};

const getAnonymizedImageUrl = (
  imageData: Image,
  baseline?: ImageUrlBaseline
): string | null => {
  const { url, s3_location: s3Location } = imageData;
  if (!s3Location) return null;

  if (s3Location.startsWith('http://') || s3Location.startsWith('https://')) {
    return s3Location;
  }

  const s3LocationChanged = Boolean(
    baseline?.initialS3Location &&
    baseline.initialS3Location !== s3Location
  );
  const urlChanged = Boolean(baseline?.initialUrl && url && url !== baseline.initialUrl);

  if (url && presignedUrlMatchesS3Location(url, s3Location)) {
    return url;
  }

  // s3_location 已更新，但 presigned url 仍指向原图时，继续等待
  if (s3LocationChanged && !urlChanged) {
    console.log('[getAnonymizedImageUrl] s3_location updated but presigned url still stale', {
      url,
      s3Location,
      initialUrl: baseline?.initialUrl,
      initialS3Location: baseline?.initialS3Location,
    });
    return null;
  }

  if (urlChanged && url) {
    return url;
  }

  if (url && !baseline) {
    return url;
  }

  return null;
};

const isAnonymizationDisplayReady = (imageData: Image, baseline?: ImageUrlBaseline): boolean => {
  return getAnonymizedImageUrl(imageData, baseline) !== null;
};

const hasAnalysisTriggers = (imageData: Image): boolean => {
  const triggers = imageData.analysis?.triggers ?? imageData.triggers ?? [];
  return Array.isArray(triggers) && triggers.length > 0;
};

async function pollForAnonymization(
  imageId: string,
  baseline?: ImageUrlBaseline
): Promise<Image> {
  for (let attempt = 1; attempt <= MAX_POLL_ATTEMPTS; attempt++) {
    const imageData = await fetchImage(imageId, true);
    console.log(`[pollForAnonymization] poll #${attempt} response:`, imageData);

    if (isAnonymizationDisplayReady(imageData, baseline)) {
      const displayUrl = getAnonymizedImageUrl(imageData, baseline);
      console.log('[pollForAnonymization] Anonymization ready:', {
        displayUrl,
        s3_location: imageData.s3_location,
        url: imageData.url,
        baseline,
      });
      return imageData;
    }

    if (imageData.s3_location) {
      console.log('[pollForAnonymization] s3_location present but display url not ready yet', {
        s3_location: imageData.s3_location,
        url: imageData.url,
        baseline,
      });
    }

    if (attempt < MAX_POLL_ATTEMPTS) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    }
  }

  throw new Error('Anonymization timeout');
}

async function pollForTriggers(imageId: string): Promise<Image> {
  for (let attempt = 1; attempt <= MAX_POLL_ATTEMPTS; attempt++) {
    const imageData = await fetchImage(imageId, true);
    console.log(`[pollForTriggers] poll #${attempt} response:`, imageData);

    if (hasAnalysisTriggers(imageData)) {
      console.log('[pollForTriggers] Triggers ready:', imageData);
      return imageData;
    }

    if (attempt < MAX_POLL_ATTEMPTS) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    }
  }

  throw new Error('Analysis timeout');
}

const TestExperienceCodeHint = ({ onFill }: { onFill: (code: string) => void }) => {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  const handleCopyAndFill = async () => {
    try {
      await navigator.clipboard.writeText(TEST_EXPERIENCE_CODE);
    } catch {
      // fallback: still fill the input even if clipboard fails
    }
    onFill(TEST_EXPERIENCE_CODE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const actionLabel = copied ? t('login.copied') : t('login.copyAndFill');

  return (
    <div className="flex items-center gap-2 min-w-0 text-xs text-gray-500">
      <span
        className="truncate min-w-0 flex-1"
        title={`${t('login.testExperienceCodeLabel')}${TEST_EXPERIENCE_CODE}`}
      >
        {t('login.testExperienceCodeLabel')}
        <span className="font-mono text-gray-600">{TEST_EXPERIENCE_CODE}</span>
      </span>
      <button
        type="button"
        onClick={handleCopyAndFill}
        title={actionLabel}
        aria-label={actionLabel}
        className="shrink-0 p-1.5 text-teal-600 hover:bg-teal-50 rounded-md transition-colors"
      >
        {copied ? (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <rect x="9" y="9" width="13" height="13" rx="2" />
            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
          </svg>
        )}
      </button>
    </div>
  );
};

// 登录组件
const LoginSection = () => {
  const { t } = useI18n();
  const [sessionKey, setSessionKey] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const { login } = useAnalysisStore();

  const handleLogin = async () => {
    if (!sessionKey.trim()) return;
    
    console.log('[handleLogin] Starting login process for user:', sessionKey);
    setIsLoggingIn(true);
    setLoginError('');
    
    // 添加延迟以显示加载状态
    await new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      // 临时设置sessionKey到localStorage以便API可以使用
      localStorage.setItem('sessionKey', sessionKey);
      
      console.log('[handleLogin] Calling userLogin API...');
      // 使用 /2.0/users/login API 进行登录
      const loginResponse: UserLoginResponse = await userLogin(sessionKey);
      console.log('[handleLogin] Login response received:', loginResponse);
      
      if (loginResponse.status === 'activated' || loginResponse.status === 'existing') {
        console.log('[Login] User login successful:', loginResponse.message);
        // 登录成功
        login(sessionKey);
      } else {
        console.log('[Login] Login failed - unexpected status:', loginResponse.status);
        setLoginError(t('login.userNotFound'));
      }
    } catch (error: any) {
      console.error('[Login] Error during login:', error);
      
      // 检查是否是404错误（用户不存在）
      if (error.message && error.message.includes('404')) {
        setLoginError(t('login.userNotFound'));
      } else {
        // 其他错误
        setLoginError(t('login.loginFailed'));
      }
    } finally {
      // 清理临时sessionKey
      localStorage.removeItem('sessionKey');
      setIsLoggingIn(false);
      console.log('[handleLogin] Login process completed');
    }
  };



  return (
    <section className="py-8 bg-white">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="text-center mb-4">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {t('login.title')}
            </h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <input
                type="text"
                id="sessionKey"
                value={sessionKey}
                onChange={(e) => setSessionKey(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                disabled={isLoggingIn}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-shadow disabled:opacity-50 bg-white"
                placeholder={t('login.placeholder')}
              />
            </div>
            
            {/* 错误信息显示 */}
            {loginError && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                {loginError}
              </div>
            )}

            <TestExperienceCodeHint onFill={setSessionKey} />

            <button
              onClick={handleLogin}
              disabled={!sessionKey.trim() || isLoggingIn}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                sessionKey.trim() && !isLoggingIn
                  ? 'bg-teal-600 hover:bg-teal-700 text-white'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isLoggingIn ? (
                <div className="flex items-center justify-center space-x-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>{t('login.verifying')}</span>
                </div>
              ) : (
                t('login.startButton')
              )}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  const { t, language, setLanguage } = useI18n();

  // 添加滑动条样式和心跳动画
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .slider::-webkit-slider-thumb {
        appearance: none;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #ffffff;
        border: 2px solid #10b981;
        cursor: pointer;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        transition: all 0.2s ease;
      }
      
      .slider::-webkit-slider-thumb:hover {
        transform: scale(1.1);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
      }
      
      .slider::-moz-range-thumb {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #ffffff;
        border: 2px solid #10b981;
        cursor: pointer;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        transition: all 0.2s ease;
      }
      
      .slider::-moz-range-thumb:hover {
        transform: scale(1.1);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
      }
      
      .slider::-webkit-slider-track {
        height: 8px;
        border-radius: 4px;
        background: transparent;
      }
      
      .slider::-moz-range-track {
        height: 8px;
        border-radius: 4px;
        background: transparent;
        border: none;
      }
      
      @keyframes heartbeat {
        0% {
          transform: scale(1);
        }
        25% {
          transform: scale(1.05);
        }
        50% {
          transform: scale(1);
        }
        75% {
          transform: scale(1.05);
        }
        100% {
          transform: scale(1);
        }
      }
      
      .heartbeat-1 {
        animation: heartbeat 2s ease-in-out 3;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const {
    // postContent,
    imageUrl,
    triggers,
    currentStep,
    isLoading,
    error,
    analysisStartTime,
    currentAnalysisTime,
    averageAnalysisTime, // 添加averageAnalysisTime
    keyword,
    isLoggedIn,
    sessionKey,
    currentImageId, // 添加currentImageId
    setImageUrl,
    setTriggers,
    setCurrentStep,
    setIsLoading,
    setError,
    setAnalysisStartTime,
    setCurrentAnalysisTime,
    setAverageAnalysisTime,
    setKeyword,
    setRawAnalysisResult,
    setCurrentImageId, // 添加setCurrentImageId
    reset,
    logout,
    initializeAuth,
  } = useAnalysisStore()

  const [imageFileName, setImageFileName] = useState<string | null>(null);
  const [cachedImageFile, setCachedImageFile] = useState<File | null>(null); // 保存图片文件用于重新分析
  const fileInputRef = useRef<HTMLInputElement>(null)

  const mainImageRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [analyzing, setAnalyzing] = useState(false);
  const [triggerStates, setTriggerStates] = useState<Record<string, {
    isAccepted: boolean;
    rating: number | null;
  }>>(() => {
    const initialState: Record<string, { isAccepted: boolean; rating: number | null }> = {};
    triggers.forEach(trigger => {
      initialState[trigger.id] = {
        isAccepted: true,
        rating: null
      };
    });
    return initialState;
  });

  // 添加编辑状态管理
  const [editingTriggers, setEditingTriggers] = useState<Record<string, {
    isEditing: boolean;
    editedTitle: string;
    editedExplanation: string;
    isSaving: boolean;
  }>>({});

  // 初始化编辑状态
  useEffect(() => {
    const initialEditState: Record<string, { isEditing: boolean; editedTitle: string; editedExplanation: string; isSaving: boolean }> = {};
    triggers.forEach(trigger => {
      if (!editingTriggers[trigger.id]) {
        initialEditState[trigger.id] = {
          isEditing: false,
          editedTitle: trigger.title,
          editedExplanation: trigger.explanation,
          isSaving: false
        };
      }
    });
    if (Object.keys(initialEditState).length > 0) {
      setEditingTriggers(prev => ({ ...prev, ...initialEditState }));
    }
  }, [triggers]);
  // const [showUndoAnimation, setShowUndoAnimation] = useState(false); // 删除未使用的状态

  // 新增：分析结果反馈的状态
  const [feedbackAccuracy, setFeedbackAccuracy] = useState(false);
  const [feedbackClarity, setFeedbackClarity] = useState(false);
  const [feedbackNovelty, setFeedbackNovelty] = useState(false);
  const [feedbackNoveltyDetail, setFeedbackNoveltyDetail] = useState('');

  // 新增：焦虑程度评分状态
  const [anxietyLevel, setAnxietyLevel] = useState(0); // 0-10的评分，默认为0

  // Add new state for user identity
  const [showNewAnalysisConfirm, setShowNewAnalysisConfirm] = useState(false);
  const [showAddTriggerForm, setShowAddTriggerForm] = useState(false);
  const [newTriggerTitle, setNewTriggerTitle] = useState('');
  const [newTriggerExplanation, setNewTriggerExplanation] = useState('');
  const [, setShowDataManagement] = useState(false);
  const [currentGad7Question, setCurrentGad7Question] = useState(0);

  const [confirmedAccurateResults, setConfirmedAccurateResults] = useState<Array<{id: string, title: string, explanation: string}>>([]);
  const [suggestionGenerationOrder, setSuggestionGenerationOrder] = useState<string[]>([]);

  
  // 建议生成时间跟踪状态
  const [suggestionStartTimes, setSuggestionStartTimes] = useState<Record<string, number>>({});
  const [currentSuggestionTimes, setCurrentSuggestionTimes] = useState<Record<string, number>>({});
  
  // 跟踪用户是否手动切换过语言
  const [hasUserManuallyChangedLanguage, setHasUserManuallyChangedLanguage] = useState(false);
  // 定义用户身份类型
  type UserIdentity = {
    gender: string;
    ageGroup: string;
    occupation: string;
    otherInfo: string;
  };

  // 跟踪图片是否已经匿名化处理
  const [isImageAnonymized, setIsImageAnonymized] = useState(false);
  const [isAnonymizationReview, setIsAnonymizationReview] = useState(false);
  const [pendingImageData, setPendingImageData] = useState<Image | null>(null);
  const [isWaitingForTriggers, setIsWaitingForTriggers] = useState(false);
  const [userIdentity, setUserIdentity] = useState<UserIdentity>(() => {
    // 从localStorage读取用户基本信息
    const savedIdentity = localStorage.getItem('userIdentity');
    if (savedIdentity) {
      try {
        return JSON.parse(savedIdentity);
      } catch (error) {
        console.error('Failed to parse saved user identity:', error);
      }
    }
    return {
      gender: '',
      ageGroup: '',
      occupation: '',
      otherInfo: ''
    };
  });

  // 用户信息提交相关状态
  const [isSubmittingUserInfo, setIsSubmittingUserInfo] = useState(false);
  const [userInfoSubmitMessage, setUserInfoSubmitMessage] = useState('');

  // 新增：用户信息展示状态
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isUserInfoLoading, setIsUserInfoLoading] = useState(true);
  
  // 新增：基本信息是否已保存状态
  const [isBasicInfoSaved, setIsBasicInfoSaved] = useState(false);

  // 新增：提交 trigger 结果的 loading 状态
  const [isSubmittingTriggers, setIsSubmittingTriggers] = useState(false);

  // 新增：重新分析的 loading 状态
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  
  // 新增：心跳动画状态
  const [showHeartbeatAnimation, setShowHeartbeatAnimation] = useState(false);
  const [showEvaluationHeartbeatAnimation, setShowEvaluationHeartbeatAnimation] = useState(false);



  // 新增：工作原理折叠状态（默认折叠）
  const [isHowItWorksExpanded, setIsHowItWorksExpanded] = useState(false);

  // 举报相关状态
  const [reportingTrigger, setReportingTrigger] = useState<string | null>(null);
  const [reportReasons, setReportReasons] = useState<{
    negativeEmotion: boolean;
    misdiagnosis: boolean;
    other: boolean;
  }>({
    negativeEmotion: false,
    misdiagnosis: false,
    other: false
  });
  const [reportOtherReason, setReportOtherReason] = useState('');
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const [reportSubmitting, setReportSubmitting] = useState(false);

  // 建议举报相关状态
  const [reportingSuggestion, setReportingSuggestion] = useState<string | null>(null);
  const [suggestionReportReasons, setSuggestionReportReasons] = useState<{
    moreAnxious: boolean;
    preachy: boolean;
    inappropriate: boolean;
    other: boolean;
  }>({
    moreAnxious: false,
    preachy: false,
    inappropriate: false,
    other: false
  });
  const [suggestionReportOtherReason, setSuggestionReportOtherReason] = useState('');
  const [suggestionReportSubmitted, setSuggestionReportSubmitted] = useState(false);
  const [suggestionReportSubmitting, setSuggestionReportSubmitting] = useState(false);

  // 新增：建议相关状态
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [suggestions, setSuggestions] = useState<Record<string, SuggestionResponse>>({});
  const [loadingSuggestions, setLoadingSuggestions] = useState<Record<string, boolean>>({});
  const [suggestionError, setSuggestionError] = useState<string | null>(null);


  // 新增：建议折叠状态
  const [expandedSuggestions, setExpandedSuggestions] = useState<Record<string, boolean>>({});

  // 新增：建议评分状态
  const [suggestionRatings, setSuggestionRatings] = useState<Record<string, number>>(() => {
    const stored = localStorage.getItem('suggestionRatings');
    return stored ? JSON.parse(stored) : {};
  });

  // 包装setSuggestionRatings以自动保存到localStorage
  const updateSuggestionRatings = (updater: React.SetStateAction<Record<string, number>>) => {
    setSuggestionRatings(prevRatings => {
      const newRatings = typeof updater === 'function' ? updater(prevRatings) : updater;
      localStorage.setItem('suggestionRatings', JSON.stringify(newRatings));
      return newRatings;
    });
  };

  // 包装setUserIdentity以自动保存到localStorage
  const updateUserIdentity = (updater: React.SetStateAction<UserIdentity>) => {
    setUserIdentity(prevIdentity => {
      const newIdentity = typeof updater === 'function' ? updater(prevIdentity) : updater;
      localStorage.setItem('userIdentity', JSON.stringify(newIdentity));
      return newIdentity;
    });
  };

  // 清空所有评价相关localStorage的函数（不包括用户基本信息）
  const clearEvaluationStorage = () => {
    localStorage.removeItem('evaluationData');
    localStorage.removeItem('suggestionRatings');
    localStorage.removeItem('interviewParticipation');
    localStorage.removeItem('interviewContactInfo');
    // 同时清空状态
    updateSuggestionRatings({});
  };

  // 清空用户基本信息的函数（仅在用户登出时调用）
  const clearUserIdentityStorage = () => {
    localStorage.removeItem('userIdentity');
  };

  // 新增：评价模块相关状态
  const [evaluationData, setEvaluationData] = useState(() => {
    // 从localStorage读取第一个焦虑程度评分作为初始值
    const storedFirstAnxietyLevel = localStorage.getItem('firstAnxietyLevel');
    const initialEmoIntensityAfter = storedFirstAnxietyLevel ? parseInt(storedFirstAnxietyLevel) : 0;
    
    // 从localStorage读取抽奖联系方式
    const storedRaffleContactInfo = localStorage.getItem('raffleContactInfo');
    const initialContactInfo = storedRaffleContactInfo || '';
    
    // 从localStorage读取评价数据
    const storedEvaluationData = localStorage.getItem('evaluationData');
    const initialEvaluationData = storedEvaluationData ? JSON.parse(storedEvaluationData) : {
      relatabilityAfter: 0,
      helpfulnessAfter: 0,
      learnabilityAfter: 0,
      comment: '',
      mostEffectiveSuggestion: ''
    };
    
    return {
      emoIntensityAfter: initialEmoIntensityAfter, // 当前焦虑程度 (0-10) - 从localStorage读取第一个评分
      relatabilityAfter: initialEvaluationData.relatabilityAfter, // 触发点准确性 (1-5)
      helpfulnessAfter: initialEvaluationData.helpfulnessAfter, // 建议有帮助性 (1-5)
      learnabilityAfter: initialEvaluationData.learnabilityAfter, // 学习能力 (1-5)
      comment: initialEvaluationData.comment, // 文字反馈
      mostEffectiveSuggestion: initialEvaluationData.mostEffectiveSuggestion, // 最有效的建议类型
      contactInfo: initialContactInfo // 联系方式 - 抽奖活动，从localStorage读取
    };
  });

  // 包装setEvaluationData以自动保存到localStorage
  const updateEvaluationData = (updater: React.SetStateAction<typeof evaluationData>) => {
    setEvaluationData(prevData => {
      const newData = typeof updater === 'function' ? updater(prevData) : updater;
      // 保存到localStorage
      const dataToSave = {
        relatabilityAfter: newData.relatabilityAfter,
        helpfulnessAfter: newData.helpfulnessAfter,
        learnabilityAfter: newData.learnabilityAfter,
        comment: newData.comment,
        mostEffectiveSuggestion: newData.mostEffectiveSuggestion
      };
      localStorage.setItem('evaluationData', JSON.stringify(dataToSave));
      return newData;
    });
  };
  const [isSubmittingEvaluation, setIsSubmittingEvaluation] = useState(false);
  const [evaluationSubmitted, setEvaluationSubmitted] = useState(false);
  const [evaluationError, setEvaluationError] = useState<string | null>(null);
  const [showContactHint, setShowContactHint] = useState(false);

  // 访谈参与相关状态
  const [interviewParticipation, setInterviewParticipation] = useState(() => {
    const stored = localStorage.getItem('interviewParticipation');
    return stored ? JSON.parse(stored) : false;
  });
  const [interviewContactInfo, setInterviewContactInfo] = useState(() => {
    const stored = localStorage.getItem('interviewContactInfo');
    return stored || '';
  });

  // 包装setInterviewParticipation以自动保存到localStorage
  const updateInterviewParticipation = (value: boolean) => {
    setInterviewParticipation(value);
    localStorage.setItem('interviewParticipation', JSON.stringify(value));
  };

  // 包装setInterviewContactInfo以自动保存到localStorage
  const updateInterviewContactInfo = (value: string) => {
    setInterviewContactInfo(value);
    localStorage.setItem('interviewContactInfo', value);
  };
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactSubmitting, setContactSubmitting] = useState(false);

  // GAD-7 自测相关状态
  const [isGad7Expanded, setIsGad7Expanded] = useState(false);
  const [gad7Answers, setGad7Answers] = useState<Record<string, number>>({});
  const [gad7Score, setGad7Score] = useState<number | null>(null);
  const [showGad7Result, setShowGad7Result] = useState(false);

  // 新增：图片预览模态框状态
  const [showImagePreview, setShowImagePreview] = useState(false);

  // 新增：图片点击处理函数
  const handleImagePreviewClick = () => {
    if (imageUrl) {
      setShowImagePreview(true);
    }
  };

  // 新增：关闭图片预览处理函数
  const handleCloseImagePreview = () => {
    setShowImagePreview(false);
  };


  // 用于标记是否已经初始化过登录状态
  const [authInitialized, setAuthInitialized] = useState(false);

  // Load user identity from localStorage on component mount
  useEffect(() => {
    const savedIdentity = localStorage.getItem('userIdentity')
    if (savedIdentity) {
      try {
        const parsedIdentity = JSON.parse(savedIdentity);
        updateUserIdentity(parsedIdentity);
        
        // 检查是否有完整的必填基本信息
        if (parsedIdentity.gender && parsedIdentity.ageGroup && parsedIdentity.occupation) {
          setIsBasicInfoSaved(true);
        }
      } catch (error) {
        console.error('Failed to parse saved user identity:', error);
      }
    }
    
    // 加载联系方式信息
    const savedContactInfo = localStorage.getItem('userContactInfo')
    if (savedContactInfo) {
      try {
        const parsedContactInfo = JSON.parse(savedContactInfo);
        
        // 自动填充抽奖联系方式
        if (parsedContactInfo.raffle_contact) {
          setEvaluationData(prev => ({
            ...prev,
            contactInfo: parsedContactInfo.raffle_contact
          }));
        }
        
        // 自动填充访谈参与状态和联系方式
        if (parsedContactInfo.interview_participation) {
          updateInterviewParticipation(true);
                      if (parsedContactInfo.interview_contact) {
              updateInterviewContactInfo(parsedContactInfo.interview_contact);
            }
        }
        
        console.log('[useEffect] Loaded contact info from localStorage:', parsedContactInfo);
      } catch (error) {
        console.error('Failed to parse saved contact info:', error);
      }
    }
    

    
    // 加载保存的建议
    const savedSuggestions = localStorage.getItem('savedSuggestions');
    if (savedSuggestions) {
      try {
        const parsedSuggestions = JSON.parse(savedSuggestions);
        if (Object.keys(parsedSuggestions).length > 0) {
          setSuggestions(parsedSuggestions);
          console.log('[useEffect] Loaded suggestions from localStorage:', parsedSuggestions);
        }
      } catch (error) {
        console.error('Failed to parse saved suggestions:', error);
      }
    }
  }, [])



  // 心跳动画控制
  useEffect(() => {
    if (analyzing || isReanalyzing) {
      // 当开始分析时，延迟500ms后开始心跳动画
      const timer = setTimeout(() => {
        setShowHeartbeatAnimation(true);
        // 3秒后停止动画
        setTimeout(() => {
          setShowHeartbeatAnimation(false);
        }, 5000);
      }, 1000);
      
      return () => {
        clearTimeout(timer);
        setShowHeartbeatAnimation(false);
      };
    } else {
      setShowHeartbeatAnimation(false);
    }
  }, [analyzing, isReanalyzing]);

  // 当焦虑程度评分变化时，更新评价部分的焦虑程度并存储到localStorage
  useEffect(() => {
    console.log('[Evaluation] Syncing anxietyLevel to evaluationData:', anxietyLevel);
    // 只有当用户还没有手动设置过评价模块的焦虑程度时，才自动同步
    // 或者当anxietyLevel从0变为非0时，强制同步
    setEvaluationData(prev => ({
      ...prev,
      emoIntensityAfter: anxietyLevel
    }));
    
    // 当anxietyLevel不为0时，存储到localStorage
    if (anxietyLevel > 0) {
      localStorage.setItem('firstAnxietyLevel', anxietyLevel.toString());
      console.log('[Evaluation] Stored first anxiety level to localStorage:', anxietyLevel);
    }
  }, [anxietyLevel]);

  // 评价提示词心跳动画控制
  useEffect(() => {
    if (currentStep === 'complete' && showSuggestions && !evaluationSubmitted && Object.keys(suggestions).length > 0) {
      const timer = setTimeout(() => {
        setShowEvaluationHeartbeatAnimation(true);
        setTimeout(() => {
          setShowEvaluationHeartbeatAnimation(false);
        }, 5000);
      }, 1000);
      
      return () => {
        clearTimeout(timer);
        setShowEvaluationHeartbeatAnimation(false);
      };
    } else {
      setShowEvaluationHeartbeatAnimation(false);
    }
  }, [currentStep, showSuggestions, evaluationSubmitted, suggestions]);

  // 防止评价提交后的自动滚动
  useEffect(() => {
    if (evaluationSubmitted) {
      // 阻止页面自动滚动，保持当前位置
      window.scrollTo({ top: window.scrollY });
    }
  }, [evaluationSubmitted]);

  // 初始化登录状态 - 从localStorage恢复sessionKey
  useEffect(() => {
    initializeAuth();
    setAuthInitialized(true);
  }, [initializeAuth])

  // 新增：当用户登录后，获取用户信息 (with caching)
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!isLoggedIn) {
        setIsUserInfoLoading(false);
        setUserInfo(null);
        return;
      }

      setIsUserInfoLoading(true);
      
      const cachedUserInfo = localStorage.getItem('cachedUserInfo');
      let hasCachedInfo = false;
      
      if (cachedUserInfo) {
        try {
          const data: UserInfo = JSON.parse(cachedUserInfo);
          setUserInfo(data);
          
          // 设置语言偏好 - 优先使用后端返回的语言设置，但只在用户没有手动切换过时才设置
          if (data.language && (data.language === 'zh' || data.language === 'en') && !hasUserManuallyChangedLanguage) {
            console.log('[fetchUserInfo] Setting language from cached user info:', data.language);
            setLanguage(data.language);
            // 同时更新localStorage中的语言设置
            localStorage.setItem('language', data.language);
          }
          
          // 只有在没有缓存信息时才设置用户身份表单信息
          setUserIdentity({
            gender: data.gender || '',
            ageGroup: data.age_range || '',
            occupation: data.occupation || '',
            otherInfo: data.other_info || '',
          });
          
          hasCachedInfo = true;
        } catch (e) {
          console.error('[fetchUserInfo] Failed to parse cached user info, fetching from API:', e);
          localStorage.removeItem('cachedUserInfo');
        }
      }

      // 无论是否有缓存，都要从后端获取最新信息（主要是为了更新 quota）
      try {
        const partialUserInfo = await getUserInfo();

        if (!partialUserInfo || !partialUserInfo.gender) {
          // 如果后端没有用户信息且没有缓存，显示表单
          if (!hasCachedInfo) {
          setUserInfo(null);
          localStorage.removeItem('cachedUserInfo');
          }
        } else {
          // 在前端将 sessionKey (即 user_id) 与后端返回的数据合并
          const fullUserInfo: UserInfo = {
            ...partialUserInfo,
            user_id: sessionKey!, // 使用 sessionKey 作为 user_id
          };

          setUserInfo(fullUserInfo);
          localStorage.setItem('cachedUserInfo', JSON.stringify(fullUserInfo));
          
          // 设置语言偏好 - 优先使用后端返回的语言设置，但只在用户没有手动切换过时才设置
          if (fullUserInfo.language && (fullUserInfo.language === 'zh' || fullUserInfo.language === 'en') && !hasUserManuallyChangedLanguage) {
            console.log('[fetchUserInfo] Setting language from API user info:', fullUserInfo.language);
            setLanguage(fullUserInfo.language);
            // 同时更新localStorage中的语言设置
            localStorage.setItem('language', fullUserInfo.language);
          }

          // 只有在没有缓存信息时才设置用户身份表单信息
          if (!hasCachedInfo) {
          updateUserIdentity({
            gender: fullUserInfo.gender || '',
            ageGroup: fullUserInfo.age_range || '',
            occupation: fullUserInfo.occupation || '',
            otherInfo: fullUserInfo.other_info || '',
          });
          }
          
          // 检查是否有完整的必填基本信息
          if (fullUserInfo.gender && fullUserInfo.age_range && fullUserInfo.occupation) {
            setIsBasicInfoSaved(true);
          }
        }
      } catch (error) {
        console.error('[fetchUserInfo] Error fetching user info from API:', error);
        // 如果有缓存信息，即使API调用失败也不清除用户信息
        if (!hasCachedInfo) {
        setUserInfo(null);
        }
      } finally {
        setIsUserInfoLoading(false);
      }
    };

    if (authInitialized) {
      fetchUserInfo();
    }
  }, [isLoggedIn, authInitialized]);

  // 监听登录状态变化，当用户登录后自动展开体验流程并滚动到分析区域
  useEffect(() => {
    if (isLoggedIn && authInitialized) {
      // 用户登录后，自动展开体验流程
      setIsHowItWorksExpanded(true);
      
      // 滚动到分析区域
      setTimeout(() => {
        const analysisSection = document.getElementById('analysis-section');
        if (analysisSection) {
          analysisSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [isLoggedIn, authInitialized]);

  // 监听登录状态变化，当用户真正登出时重置用户身份信息
  useEffect(() => {
    // 只有在认证已初始化且用户真正登出时才重置用户身份信息
    if (authInitialized && !isLoggedIn) {
      updateUserIdentity({
        gender: '',
        ageGroup: '',
        occupation: '',
        otherInfo: ''
      });
      // 用户登出时也清除缓存
      localStorage.removeItem('cachedUserInfo');
      // 清空用户基本信息
      clearUserIdentityStorage();
      // 重置基本信息保存状态
      setIsBasicInfoSaved(false);
      // 清除语言偏好，恢复默认语言
      // 可以选择恢复到默认语言或保持当前语言
      // setLanguage('zh'); // 如果想要恢复到默认语言，取消注释这行
    }
  }, [isLoggedIn, authInitialized])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((_entry) => {
          // entry参数重命名为_entry表示未使用
        });
      },
      { threshold: 0.1 }
    );

    if (mainImageRef.current) {
      observer.observe(mainImageRef.current)
    }

    return () => {
      if (mainImageRef.current) {
        observer.unobserve(mainImageRef.current)
      }
    }
  }, [])

  // 添加时间跟踪的 useEffect
  useEffect(() => {
    if (analyzing && analysisStartTime) {
      const interval = setInterval(() => {
        setCurrentAnalysisTime(Date.now() - analysisStartTime)
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [analyzing, analysisStartTime])

  // 建议生成时间跟踪的 useEffect
  useEffect(() => {
    const activeSuggestions = Object.keys(suggestionStartTimes).filter(
      type => loadingSuggestions[type as keyof typeof loadingSuggestions]
    );

    if (activeSuggestions.length > 0) {
      const interval = setInterval(() => {
        const newTimes: Record<string, number> = {};
        activeSuggestions.forEach(type => {
          const startTime = suggestionStartTimes[type];
          if (startTime) {
            newTimes[type] = Date.now() - startTime;
          }
        });
        setCurrentSuggestionTimes(prev => ({ ...prev, ...newTimes }));
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [suggestionStartTimes, loadingSuggestions]);

  // 更新平均时间的 useEffect
  useEffect(() => {
    if (currentAnalysisTime && !analyzing) {
      // 从 localStorage 获取历史时间
      const historyTimes = JSON.parse(localStorage.getItem('analysisTimes') || '[]')
      // 添加新的分析时间
      historyTimes.push(currentAnalysisTime)
      // 计算新的平均值
      const newAverage = historyTimes.reduce((sum: number, time: number) => sum + time, 0) / historyTimes.length
      // 保存历史时间和新的平均值
      localStorage.setItem('analysisTimes', JSON.stringify(historyTimes))
      localStorage.setItem('averageAnalysisTime', newAverage.toString())
      setAverageAnalysisTime(newAverage)
    }
  }, [currentAnalysisTime, analyzing])

  // 从 localStorage 获取平均时间
  useEffect(() => {
    const savedTime = localStorage.getItem('averageAnalysisTime')
    if (savedTime) {
      setAverageAnalysisTime(parseFloat(savedTime))
    }
  }, [])

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    
    const file = e.dataTransfer.files[0]
    if (file) {
      await processSelectedImage(file)
    }
  }

  const loadImagePreview = (file: File) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const result = e.target?.result as string
      setImageUrl(result)
      setAnxietyLevel(0)
      setSuggestionRatings({})
      clearEvaluationStorage()
    }
    reader.readAsDataURL(file)
  }

  const processSelectedImage = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError(t('errors.invalidFileType'))
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      setError(t('errors.fileSizeLimit'))
      return
    }

    try {
      const compressedFile = await compressImage(file)
      setImageFileName(compressedFile.name)
      setCachedImageFile(compressedFile)
      loadImagePreview(compressedFile)
    } catch (error) {
      console.error('Image compression failed:', error)
      setImageFileName(file.name)
      setCachedImageFile(file)
      loadImagePreview(file)
    }
  }

  const handleSampleImageSelect = async (imagePath: string) => {
    try {
      const response = await fetch(imagePath)
      if (!response.ok) {
        throw new Error(`Failed to load sample image: ${imagePath}`)
      }

      const blob = await response.blob()
      const fileName = imagePath.split('/').pop() || 'sample-image.jpg'
      const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' })
      await processSelectedImage(file)
    } catch (error) {
      console.error('Failed to load sample image:', error)
      setError(t('errors.sampleImageLoadFailed'))
    }
  }

  // 图片压缩函数
  const compressImage = (file: File): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const img = new Image();
      
      img.onload = () => {
        // 计算压缩后的尺寸（原来的一半）
        const maxWidth = img.width / 2;
        const maxHeight = img.height / 2;
        
        // 保持宽高比
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
        
        // 设置canvas尺寸
        canvas.width = width;
        canvas.height = height;
        
        // 绘制压缩后的图片
        ctx.drawImage(img, 0, 0, width, height);
        
        // 转换为Blob
        canvas.toBlob((blob) => {
          if (blob) {
            // 创建新的File对象
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          } else {
            resolve(file); // 如果压缩失败，返回原文件
          }
        }, file.type, 0.8); // 0.8的压缩质量
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      await processSelectedImage(file)
      e.target.value = ''
    }
  }

  const applyAnalysisFromImageData = (imageData: Image) => {
    const anonymizedUrl = getAnonymizedImageUrl(imageData);
    if (anonymizedUrl) {
      setImageUrl(anonymizedUrl);
      setIsImageAnonymized(true);
    }

    const triggers = imageData.analysis?.triggers ?? imageData.triggers ?? [];
    const formattedTriggers = triggers.map((trigger: any, index: number) => ({
      id: trigger.id || trigger.trigger_id || `trigger_${index}`,
      trigger_id: trigger.id || trigger.trigger_id,
      title: trigger.title || `Trigger ${index + 1}`,
      explanation: trigger.explanation || '',
      type: trigger.type || 'general',
      impact: trigger.impact || 'medium',
      confidence: 1.0,
      isAccepted: false,
      rating: null
    }));

    setRawAnalysisResult(imageData.analysis_text || '');
    setTriggers(formattedTriggers);
    setKeyword(imageData.analysis?.keyword || imageData.keyword || 'unknown');
    setIsAnonymizationReview(false);
    setPendingImageData(null);
    setCurrentStep('analysis');

    console.log('[applyAnalysisFromImageData] Applied analysis:', {
      keyword: imageData.analysis?.keyword || imageData.keyword,
      triggers: formattedTriggers,
      rawAnalysisResult: imageData.analysis_text,
      imageUrl: anonymizedUrl,
    });
  };

  const showAnonymizationReview = (imageData: Image, baseline?: ImageUrlBaseline) => {
    const anonymizedUrl = getAnonymizedImageUrl(imageData, baseline);
    console.log('[showAnonymizationReview] Resolved display url:', {
      anonymizedUrl,
      url: imageData.url,
      s3_location: imageData.s3_location,
      baseline,
    });

    if (anonymizedUrl) {
      setImageUrl(anonymizedUrl);
      setIsImageAnonymized(true);
    }

    setPendingImageData(imageData);
    setIsAnonymizationReview(true);
    setCurrentStep('initial');
    console.log('[showAnonymizationReview] Ready for user verification:', imageData);
  };

  const handleVerifyAnonymization = async () => {
    if (!currentImageId) {
      return;
    }

    setIsWaitingForTriggers(true);
    setError(null);

    try {
      const latestData = pendingImageData?.image_id === currentImageId
        ? pendingImageData
        : await fetchImage(currentImageId, true);
      console.log('[handleVerifyAnonymization] Latest poll response:', latestData);
      setPendingImageData(latestData);

      const latestDisplayUrl = getAnonymizedImageUrl(latestData);
      if (latestDisplayUrl) {
        setImageUrl(latestDisplayUrl);
        setIsImageAnonymized(true);
      }

      const imageData = hasAnalysisTriggers(latestData)
        ? latestData
        : await pollForTriggers(currentImageId);

      applyAnalysisFromImageData(imageData);

      if (!isGad7Expanded) {
        setTimeout(() => {
          const resultsTitle = document.querySelector('[data-section="results-title"]');
          resultsTitle?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setError(`${t('errors.analysisFailed')}: ${errorMessage}`);
      console.error('[handleVerifyAnonymization] Failed:', error);
    } finally {
      setIsWaitingForTriggers(false);
    }
  };



  const handlePostSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!imageUrl) {
      setError(t('analysis.imageUpload.selectImage'));
      return;
    }

    // 清理之前的分析结果和建议状态
    setTriggers([]);
    setKeyword('');
    setRawAnalysisResult('');
    setError('');
    setCurrentImageId('');
    setIsAnonymizationReview(false);
    setPendingImageData(null);
    setIsWaitingForTriggers(false);
    setIsImageAnonymized(false);
    
    // 清理建议相关状态 - 确保每张图片都有独立的建议
    setShowSuggestions(false);
    setSuggestions({});
    setLoadingSuggestions({});
    setSuggestionError(null);
    setExpandedSuggestions({});
    setSuggestionRatings({});
    
    // 清空localStorage中的建议
    localStorage.removeItem('savedSuggestions');
    
    // 清空评价相关的localStorage
    clearEvaluationStorage();
    
    // 清理评价相关状态，但保留从localStorage读取的第一个焦虑程度评分和抽奖联系方式
    const storedFirstAnxietyLevel = localStorage.getItem('firstAnxietyLevel');
    const initialEmoIntensityAfter = storedFirstAnxietyLevel ? parseInt(storedFirstAnxietyLevel) : 0;
    const storedRaffleContactInfo = localStorage.getItem('raffleContactInfo');
    const initialContactInfo = storedRaffleContactInfo || '';
    
    updateEvaluationData({
      emoIntensityAfter: initialEmoIntensityAfter,
      relatabilityAfter: 0,
      helpfulnessAfter: 0,
      learnabilityAfter: 0,
      comment: '',
      mostEffectiveSuggestion: '',
      contactInfo: initialContactInfo
    });
    setEvaluationSubmitted(false);
    setEvaluationError(null);

    setIsLoading(true)
    setError(null)
    setAnalyzing(true)
    setAnalysisStartTime(Date.now())

    console.log('[handlePostSubmit] Started analysis flow', {
      imageFileName,
      anxietyLevel,
      hasCachedImageFile: Boolean(cachedImageFile),
    })

    try {
      // 1. 使用 imageApi 上传图片
      if (!imageUrl) {
        throw new Error(t('errors.uploadRequired'));
      }

      // 将base64转换为File对象
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const file = new File([blob], imageFileName || 'uploaded-image.jpg', { type: blob.type });

      // 如果没有缓存的文件，保存当前File对象用于重新分析
      if (!cachedImageFile) {
        setCachedImageFile(file);
      }

      // 使用 imageApi 的 uploadImage 函数
      const uploadResult = await uploadImage(file);
      console.log('[handlePostSubmit] uploadImage response:', uploadResult);
      const imageId = uploadResult.image_id;

      // 存储当前分析的image_id到store
      setCurrentImageId(imageId);

      const initialImageData = await fetchImage(imageId, true);
      const imageUrlBaseline: ImageUrlBaseline = {
        initialS3Location: initialImageData.s3_location,
        initialUrl: initialImageData.url,
      };
      console.log('[handlePostSubmit] Initial image snapshot for anonymization polling:', imageUrlBaseline);

      // 更新用户quota信息（如果返回了remaining_quota）
      if (typeof uploadResult.remaining_quota === 'number' && userInfo) {
        const updatedUserInfo = {
          ...userInfo,
          quota: uploadResult.remaining_quota
        };
        setUserInfo(updatedUserInfo);
        // 同时更新缓存
        localStorage.setItem('cachedUserInfo', JSON.stringify(updatedUserInfo));
        console.log(`[handlePostSubmit] Updated user quota to: ${uploadResult.remaining_quota}`);
      }

      // 2. 立即上传用户的焦虑程度评分到数据库
      try {
        const infoBeforeResult = await updateImageInfoBefore(imageId, anxietyLevel);
        console.log('[handlePostSubmit] updateImageInfoBefore response:', infoBeforeResult);
        console.log(`[handlePostSubmit] Successfully uploaded anxiety level: ${anxietyLevel} for image: ${imageId}`);
      } catch (error) {
        console.error('[handlePostSubmit] Failed to upload anxiety level:', error);
        // 不阻止分析流程，只记录错误
      }

      // 3. 自动保存用户填写的基本信息（如果用户填写了信息但忘记点击提交）
      try {
        // 检查用户是否填写了基本信息
        if (userIdentity.gender || userIdentity.ageGroup || userIdentity.occupation || userIdentity.otherInfo) {
          console.log('[handlePostSubmit] Auto-saving user info:', userIdentity);
          
          const userInfoRequest: UserInfoRequest = {
            gender: userIdentity.gender,
            age_range: userIdentity.ageGroup,
            occupation: userIdentity.occupation,
            language: language,
            other_info: userIdentity.otherInfo
          };

          const userInfoResponse = await createUserInfo(userInfoRequest);
          console.log('[handlePostSubmit] User info auto-saved:', userInfoResponse);
          
          // 更新用户信息状态
          setTimeout(async () => {
            try {
              const updatedUserInfo = await getUserInfo();
              setUserInfo(updatedUserInfo);
              localStorage.setItem('cachedUserInfo', JSON.stringify(updatedUserInfo));
              console.log('[handlePostSubmit] Updated user info after auto-save:', updatedUserInfo);
            } catch (error) {
              console.error('[handlePostSubmit] Failed to fetch updated user info after auto-save:', error);
            }
          }, 1000);
        } else {
          console.log('[handlePostSubmit] No user info to auto-save');
        }
      } catch (error) {
        console.error('[handlePostSubmit] Failed to auto-save user info:', error);
        // 不阻止分析流程，只记录错误
      }

      // 4. 轮询等待匿名化处理完成，且 presigned url 已指向匿名化后的图片
      const anonymizedImageData = await pollForAnonymization(imageId, imageUrlBaseline);
      console.log('[handlePostSubmit] Anonymization poll complete:', anonymizedImageData);
      showAnonymizationReview(anonymizedImageData, imageUrlBaseline);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setError(`${t('errors.analysisFailed')}: ${errorMessage}`);
      console.error('[handlePostSubmit] Analysis failed:', error);
    } finally {
      setIsLoading(false);
      setAnalyzing(false);
    }
  }

  const handleRatingSubmit = async () => {
    // 设置提交状态为 loading
    setIsSubmittingTriggers(true);
    
    try {
    const hasAcceptedTriggers = triggers.some(trigger => {
      const state = triggerStates[trigger.id];
      return state && state.isAccepted;
    });

    if (!hasAcceptedTriggers) {
        // 没有接受的triggers
    }

    const updatedTriggerStates = { ...triggerStates };
    triggers.forEach(trigger => {
      if (!updatedTriggerStates[trigger.id]) {
        // 如果trigger没有状态，设置默认状态
        updatedTriggerStates[trigger.id] = {
          isAccepted: false,
          rating: 0 
        };
      } else if (updatedTriggerStates[trigger.id].rating === null) {
        // 如果rating为null，只更新rating，保持isAccepted状态
        updatedTriggerStates[trigger.id] = {
          ...updatedTriggerStates[trigger.id],
          rating: 0 
        };
      }
    });
    setTriggerStates(updatedTriggerStates);

      // 找出用户不接受的触发因素，准备删除
      const triggersToDelete = triggers.filter(trigger => {
        const state = updatedTriggerStates[trigger.id];
        const hasRealTriggerId = trigger.trigger_id && !trigger.trigger_id.startsWith('trigger_');
        // 删除有真实 trigger_id 且用户不接受的 trigger（包括自定义trigger）
        return hasRealTriggerId && state && !state.isAccepted;
      });

      // 如果用户已登录（有 sessionKey），执行 trigger 删除和更新操作
      if (sessionKey && currentImageId) {
        // 处理编辑过的trigger更新
        const triggersToUpdate = triggers.filter(trigger => {
          const editState = editingTriggers[trigger.id];
          const hasRealTriggerId = trigger.trigger_id && !trigger.trigger_id.startsWith('trigger_');
          const state = updatedTriggerStates[trigger.id];
          
          // 只更新有真实trigger_id、被接受、且内容有变化的trigger
          return hasRealTriggerId && 
                 state && state.isAccepted && 
                 editState && 
                 (editState.editedTitle !== trigger.title || editState.editedExplanation !== trigger.explanation);
        });

        // 更新编辑过的triggers
        if (triggersToUpdate.length > 0) {
          try {
            // 并行更新所有被编辑的 triggers
            const updatePromises = triggersToUpdate.map(async (trigger) => {
              try {
                const editState = editingTriggers[trigger.id];
                               const updateData: TriggerUpdate = {
                 image_id: currentImageId,
                 title: editState.editedTitle,
                 explanation: editState.editedExplanation
               };
                
                await updateTrigger(trigger.trigger_id!, updateData);
                return { success: true, triggerId: trigger.trigger_id };
              } catch (error) {
                console.error(`[handleRatingSubmit] Failed to update trigger ${trigger.trigger_id}:`, error);
                return { success: false, triggerId: trigger.trigger_id, error };
              }
            });

            // 等待所有更新操作完成
            await Promise.allSettled(updatePromises);
          } catch (error) {
            console.error('[handleRatingSubmit] Error during trigger updates:', error);
          }
        }

        // 处理需要删除的triggers
        if (triggersToDelete.length > 0) {
          try {
            // 并行删除所有不被接受的 triggers
            const deletePromises = triggersToDelete.map(async (trigger) => {
              try {
                const deleteData: TriggerDelete = {
                  image_id: currentImageId
                };
                
                await deleteTrigger(trigger.trigger_id!, deleteData);
                return { success: true, triggerId: trigger.trigger_id };
              } catch (error) {
                console.error(`[handleRatingSubmit] Failed to delete trigger ${trigger.trigger_id}:`, error);
                return { success: false, triggerId: trigger.trigger_id, error };
              }
            });

            // 等待所有删除操作完成
            await Promise.allSettled(deletePromises);
          } catch (error) {
            console.error('[handleRatingSubmit] Error during trigger deletion:', error);
          }
        }
      }

    setCurrentStep('complete');
      
      const savedPostId = await saveAnalysis();
      if (savedPostId) {
        // Clear feedback states after successful save
        setFeedbackAccuracy(false);
        setFeedbackClarity(false);
        setFeedbackNovelty(false);
        setFeedbackNoveltyDetail('');
        // 保存用户确认的准确结果
        const accurateResults = triggers.filter(trigger => {
          const state = triggerStates[trigger.id];
          return state && state.isAccepted;
        }).map(trigger => ({
          id: trigger.id,
          title: trigger.title,
          explanation: trigger.explanation
        }));
        setConfirmedAccurateResults(accurateResults);
        
        // 自动展示缓解建议
        setShowSuggestions(true);
        // 显示数据管理模块
        setShowDataManagement(true);

        // 自动生成所有三种建议，并随机排序
        const suggestionTypes: ('cognitive_reappraisal' | 'problem_focus_coping' | 'media_regulation')[] = [
          'cognitive_reappraisal', 
          'problem_focus_coping', 
          'media_regulation'
        ];
        // 随机打乱建议类型顺序
        const shuffledTypes = [...suggestionTypes].sort(() => Math.random() - 0.5);
        setSuggestionGenerationOrder(shuffledTypes);
        // 生成所有建议
        handleGenerateAllSuggestions();
        
        // 滚动到缓解焦虑建议组件位置
        setTimeout(() => {
          const suggestionsSection = document.querySelector('[data-section="suggestions"]');
          if (suggestionsSection) {
            suggestionsSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    } catch (error) {
      console.error("[handleRatingSubmit] Submission failed:", error);
      // 可以在这里添加错误提示
              setError(t('errors.submissionFailed'));
    } finally {
      // 无论成功还是失败，都要重置 loading 状态
      setIsSubmittingTriggers(false);
    }
  }

  const handleAcceptanceChange = (triggerId: string, accepted: boolean) => {
    setTriggerStates(prev => {
      const newState = {
        ...prev,
        [triggerId]: {
          ...prev[triggerId],
          isAccepted: accepted,
          rating: accepted ? 1 : 0
        }
      };
      return newState;
    });
  };

  // 编辑相关处理函数
  const handleEditTrigger = (triggerId: string) => {
    setEditingTriggers(prev => ({
      ...prev,
      [triggerId]: {
        ...prev[triggerId],
        isEditing: true
      }
    }));
  };

  const handleSaveEdit = async (triggerId: string) => {
    const editState = editingTriggers[triggerId];
    if (!editState) return;

    // 设置保存状态
    setEditingTriggers(prev => ({
      ...prev,
      [triggerId]: {
        ...prev[triggerId],
        isSaving: true
      }
    }));

    try {
      // 如果用户已登录且有数据库中的trigger_id，则更新数据库
      const trigger = triggers.find(t => t.id === triggerId);
      if (isLoggedIn && trigger?.trigger_id && currentImageId) {
        console.log('[handleSaveEdit] Updating trigger in database:', {
          triggerId: trigger.trigger_id,
          imageId: currentImageId,
          title: editState.editedTitle,
          explanation: editState.editedExplanation
        });

        await updateTrigger(trigger.trigger_id, {
          image_id: currentImageId,
          title: editState.editedTitle,
          explanation: editState.editedExplanation
        });

        console.log('[handleSaveEdit] Successfully updated trigger in database');
      }

      // 更新store中的trigger - 使用store的updateTrigger方法
      const storeUpdateTrigger = useAnalysisStore.getState().updateTrigger;
      storeUpdateTrigger(triggerId, {
        title: editState.editedTitle,
        explanation: editState.editedExplanation
      });
      
      // 退出编辑模式
      setEditingTriggers(prev => ({
        ...prev,
        [triggerId]: {
          ...prev[triggerId],
          isEditing: false,
          isSaving: false
        }
      }));

      // 编辑并保存后自动勾选采纳checkbox
      handleAcceptanceChange(triggerId, true);

      console.log('[handleSaveEdit] Successfully updated trigger in store and set as accepted');
    } catch (error) {
      console.error('[handleSaveEdit] Failed to update trigger:', error);
      
      // 重置保存状态
      setEditingTriggers(prev => ({
        ...prev,
        [triggerId]: {
          ...prev[triggerId],
          isSaving: false
        }
      }));
      
      // 显示错误消息，但不影响编辑状态
      const errorMessage = error instanceof Error ? error.message : t('errors.analysisFailed');
      alert(`${t('errors.analysisFailed')}: ${errorMessage}`);
    }
  };

  const handleCancelEdit = (triggerId: string) => {
    const originalTrigger = triggers.find(t => t.id === triggerId);
    if (originalTrigger) {
      setEditingTriggers(prev => ({
        ...prev,
        [triggerId]: {
          isEditing: false,
          editedTitle: originalTrigger.title,
          editedExplanation: originalTrigger.explanation,
          isSaving: false
        }
      }));
    }
  };

  const handleTitleChange = (triggerId: string, newTitle: string) => {
    setEditingTriggers(prev => ({
      ...prev,
      [triggerId]: {
        ...prev[triggerId],
        editedTitle: newTitle
      }
    }));
  };

  const handleExplanationChange = (triggerId: string, newExplanation: string) => {
    setEditingTriggers(prev => ({
      ...prev,
      [triggerId]: {
        ...prev[triggerId],
        editedExplanation: newExplanation
      }
    }));
  };

  // 举报相关处理函数
  const handleReportTrigger = (triggerId: string) => {
    setReportingTrigger(triggerId);
    // 重置举报状态
    setReportReasons({
      negativeEmotion: false,
      misdiagnosis: false,
      other: false
    });
    setReportOtherReason('');
    setReportSubmitted(false);
    setReportSubmitting(false);
  };

  const handleReportReasonChange = (reason: keyof typeof reportReasons, checked: boolean) => {
    setReportReasons(prev => ({
      ...prev,
      [reason]: checked
    }));
  };

  const handleCancelReport = () => {
    setReportingTrigger(null);
  };

  // 建议举报相关处理函数
  const handleReportSuggestion = (suggestionType: string) => {
    setReportingSuggestion(suggestionType);
    // 重置举报状态
    setSuggestionReportReasons({
      moreAnxious: false,
      preachy: false,
      inappropriate: false,
      other: false
    });
    setSuggestionReportOtherReason('');
    setSuggestionReportSubmitted(false);
    setSuggestionReportSubmitting(false);
  };

  const handleSuggestionReportReasonChange = (reason: keyof typeof suggestionReportReasons, checked: boolean) => {
    setSuggestionReportReasons(prev => ({
      ...prev,
      [reason]: checked
    }));
  };

  // 处理建议评分变化
  const handleSuggestionRatingChange = async (suggestionType: string, rating: number) => {
    // 更新本地状态
    updateSuggestionRatings(prev => ({
      ...prev,
      [suggestionType]: rating
    }));

    // 调用API存储评分结果
    try {
      if (currentImageId && sessionKey) {
        console.log('[handleSuggestionRatingChange] Submitting rating for suggestion:', {
          suggestionType,
          rating,
          imageId: currentImageId
        });

        const ratingData = {
          type: suggestionType as 'cognitive_reappraisal' | 'problem_focus_coping' | 'media_regulation',
          rating
        };

        await rateSuggestion(currentImageId, ratingData);
        console.log('[handleSuggestionRatingChange] Successfully submitted rating to backend');
      }
    } catch (error) {
      console.error('[handleSuggestionRatingChange] Error submitting rating:', error);
      // 评分失败时，可以选择回滚本地状态或显示错误提示
      // 这里我们保持本地状态不变，因为用户已经看到了评分按钮的视觉反馈
    }
  };

  const handleCancelSuggestionReport = () => {
    setReportingSuggestion(null);
  };

  const handleSubmitSuggestionReport = async () => {
    setSuggestionReportSubmitting(true);
    
    try {
      if (reportingSuggestion && sessionKey && currentImageId) {
        
        // 构造举报原因字符串
        const selectedReasons = [];
        if (suggestionReportReasons.moreAnxious) {
          selectedReasons.push(t('report.reasons.moreAnxious'));
        }
        if (suggestionReportReasons.preachy) {
          selectedReasons.push(t('report.reasons.preachy'));
        }
        if (suggestionReportReasons.inappropriate) {
          selectedReasons.push(t('report.reasons.inappropriate'));
        }
        if (suggestionReportReasons.other) {
          if (suggestionReportOtherReason.trim()) {
            selectedReasons.push(`${t('report.reasons.other')}: ${suggestionReportOtherReason.trim()}`);
          } else {
            selectedReasons.push(t('report.reasons.other'));
          }
        }
        
        // 如果没有选择任何原因，使用默认描述
        const reportReason = selectedReasons.length > 0 
          ? selectedReasons.join('; ') 
          : t('errors.userReportedNoReason');

        console.log('[handleSubmitSuggestionReport] Submitting report for suggestion:', {
          suggestionType: reportingSuggestion,
          imageId: currentImageId,
          reportReason,
          rawReasons: suggestionReportReasons,
          otherReason: suggestionReportOtherReason
        });

        // 调用建议举报API
        const reportData = {
          type: reportingSuggestion as 'cognitive_reappraisal' | 'problem_focus_coping' | 'media_regulation',
          report_content: reportReason
        };

        await reportSuggestion(currentImageId, reportData);
        console.log('[handleSubmitSuggestionReport] Successfully reported suggestion to backend');
      }
      
      setSuggestionReportSubmitted(true);
      console.log('[handleSubmitSuggestionReport] Suggestion report submitted successfully');
      
      // 举报成功后，从本地状态中移除该建议
      if (reportingSuggestion) {
        setSuggestions(prev => {
          const newSuggestions = { ...prev };
          delete newSuggestions[reportingSuggestion];
          return newSuggestions;
        });
      }
      
    } catch (error) {
      console.error('[handleSubmitSuggestionReport] Error submitting suggestion report:', error);
      // 显示错误提示
      setSuggestionError(t('complete.suggestions.error'));
    } finally {
      setSuggestionReportSubmitting(false);
      // 延迟关闭弹框
      setTimeout(() => {
        setReportingSuggestion(null);
        setSuggestionReportSubmitted(false);
      }, 2000);
    }
  };

  const handleSubmitReport = async () => {
    setReportSubmitting(true);
    
    try {
      const trigger = triggers.find(t => t.id === reportingTrigger);
      if (trigger && sessionKey && currentImageId && trigger.trigger_id) {
        
        // 构造举报原因字符串
        const selectedReasons = [];
        if (reportReasons.negativeEmotion) {
          selectedReasons.push(t('report.reasons.negativeEmotion'));
        }
        if (reportReasons.misdiagnosis) {
          selectedReasons.push(t('report.reasons.misdiagnosis'));
        }
        if (reportReasons.other) {
          if (reportOtherReason.trim()) {
            selectedReasons.push(`${t('report.reasons.other')}: ${reportOtherReason.trim()}`);
          } else {
            selectedReasons.push(t('report.reasons.other'));
          }
        }
        
        // 如果没有选择任何原因，使用默认描述
        const reportReason = selectedReasons.length > 0 
          ? selectedReasons.join('; ') 
          : t('errors.userReportedNoReason');

        console.log('[handleSubmitReport] Submitting report for trigger:', {
          triggerId: trigger.trigger_id,
          imageId: currentImageId,
          reportReason,
          rawReasons: reportReasons,
          otherReason: reportOtherReason
        });

        // 调用API提交举报 - 使用deleteTrigger API并设置is_reported=true
        if (isLoggedIn && trigger.trigger_id && currentImageId) {
          try {
            const deleteData: TriggerDelete = {
              image_id: currentImageId,
              is_reported: true,
              report_reason: reportReason
            };
            
            await deleteTrigger(trigger.trigger_id, deleteData);
            console.log('[handleSubmitReport] Successfully reported trigger to backend');
          } catch (error) {
            console.error('[handleSubmitReport] Failed to report trigger to backend:', error);
            // 即使后端失败，也继续显示成功消息，因为这是用户体验的一部分
          }
        }
      }
      
      // 模拟提交延迟以提供更好的用户体验
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setReportSubmitted(true);
      console.log('[handleSubmitReport] Report submitted successfully');
      
      // 举报成功后，从本地状态中移除该trigger
      // 因为API会将trigger从image的triggers_list中移除
      if (trigger) {
        const filteredTriggers = triggers.filter(t => t.id !== trigger.id);
        setTriggers(filteredTriggers);
        
        // 同时从triggerStates中移除
        setTriggerStates(prevStates => {
          const newStates = { ...prevStates };
          delete newStates[trigger.id];
          return newStates;
        });
        
        // 从editingTriggers中移除
        setEditingTriggers(prevEditingTriggers => {
          const newEditingTriggers = { ...prevEditingTriggers };
          delete newEditingTriggers[trigger.id];
          return newEditingTriggers;
        });
        
        console.log('[handleSubmitReport] Removed reported trigger from local state');
      }
      
      // 2秒后自动关闭弹框
      setTimeout(() => {
        setReportingTrigger(null);
      }, 2000);
    } catch (error) {
      console.error('[handleSubmitReport] Error submitting report:', error);
    } finally {
      setReportSubmitting(false);
    }
  };


  const saveAnalysis = async (): Promise<string | null> => { // Return postId or null
    try {
      console.log('[saveAnalysis] Starting save process');
      
      // 找出用户不接受的触发因素，准备删除
      const triggersToDelete = triggers.filter(trigger => {
        const state = triggerStates[trigger.id];
        const hasRealTriggerId = trigger.trigger_id && !trigger.trigger_id.startsWith('trigger_');
        // 只删除有真实 trigger_id 且用户不接受的 trigger
        return hasRealTriggerId && state && !state.isAccepted;
      });

      // 找出被用户接受的触发因素，用于本地存储，包含编辑后的内容
      const acceptedTriggers = triggers.filter(trigger => {
        const state = triggerStates[trigger.id];
        return state && state.isAccepted;
      }).map(trigger => {
        const state = triggerStates[trigger.id];
        const editState = editingTriggers[trigger.id];
        
        // 如果用户编辑过，使用编辑后的内容，否则使用原始内容
        return {
          ...trigger,
          title: editState ? editState.editedTitle : trigger.title,
          explanation: editState ? editState.editedExplanation : trigger.explanation,
          isAccepted: state.isAccepted,
          rating: state.rating
        };
      });
      
      console.log(`[saveAnalysis] Processing ${triggers.length} total triggers: ${acceptedTriggers.length} accepted, ${triggersToDelete.length} to delete`);

      const postId = Date.now().toString();
      
      // Retrieve rawAnalysisResult from the store
      const currentRawAnalysisResult = useAnalysisStore.getState().rawAnalysisResult;

      // 如果用户已登录（有 sessionKey），执行 trigger 删除操作
      if (sessionKey && currentImageId) {
        console.log('[saveAnalysis] User logged in, skipping local storage to avoid duplicates');
        return postId;
      }

      // 如果用户未登录，保存到本地存储
      console.log('[saveAnalysis] User not logged in, saving to local storage');
      await saveAnalysisData(
        postId, 
        keyword || 'unknown', 
        acceptedTriggers, // 只保存被接受的 trigger 到本地存储
        imageUrl || undefined,
        imageFileName || undefined, 
        sessionKey || '', // 使用sessionKey而不是subjectId，如果为null则使用空字符串                     
        feedbackAccuracy,             
        feedbackClarity,              
        feedbackNovelty,              
        feedbackNoveltyDetail,        
        currentRawAnalysisResult, // Pass rawAnalysisResult from store
        anxietyLevel // 传递焦虑程度评分
      );

      return postId; // Return postId on success
    } catch (err) {
      console.error('[saveAnalysis] Failed to save analysis:', err);
      return null; // Return null on failure
    }
  };






  // 用户信息提交处理函数
  const handleUserInfoSubmit = async () => {
    if (!userIdentity.gender || !userIdentity.ageGroup || !userIdentity.occupation) {
      setUserInfoSubmitMessage(t('analysis.userInfoRequired'));
      return;
    }

    setIsSubmittingUserInfo(true);
    setUserInfoSubmitMessage('');

    try {
      const userInfoRequest: UserInfoRequest = {
        gender: userIdentity.gender,
        age_range: userIdentity.ageGroup,
        occupation: userIdentity.occupation,
        language: language,
        other_info: userIdentity.otherInfo
      };

      const response = await createUserInfo(userInfoRequest);
      console.log('[handleUserInfoSubmit] User info created:', response);
      
      setUserInfoSubmitMessage(t('analysis.userInfoSaved'));
      setIsBasicInfoSaved(true); // 标记基本信息已保存
      
      // 重新获取用户信息以更新状态
      setTimeout(async () => {
        try {
          const updatedUserInfo = await getUserInfo();
          setUserInfo(updatedUserInfo);
          localStorage.setItem('cachedUserInfo', JSON.stringify(updatedUserInfo));
          console.log('[handleUserInfoSubmit] Updated user info:', updatedUserInfo);
        } catch (error) {
          console.error('[handleUserInfoSubmit] Failed to fetch updated user info:', error);
        }
      }, 1000);
      
    } catch (error) {
      console.error('[handleUserInfoSubmit] Error:', error);
      setUserInfoSubmitMessage(t('analysis.userInfoError'));
    } finally {
      setIsSubmittingUserInfo(false);
    }
  };



  // 新增：挨个生成所有建议的函数
  const handleGenerateAllSuggestions = async () => {
    if (!currentImageId) {
      console.error('[handleGenerateAllSuggestions] No current image ID');
      return;
    }

    // 如果已经有建议，不重复生成
    if (Object.keys(suggestions).length > 0) {
      return;
    }

    setSuggestionError(null);
    const suggestionTypes: ('cognitive_reappraisal' | 'problem_focus_coping' | 'media_regulation')[] = [
      'cognitive_reappraisal', 
      'problem_focus_coping', 
      'media_regulation'
    ];

    // 设置所有建议类型为加载状态
    setLoadingSuggestions({
      cognitive_reappraisal: true,
      problem_focus_coping: true,
      media_regulation: true
    });

    // 记录开始时间
    const startTime = Date.now();
    setSuggestionStartTimes({
      cognitive_reappraisal: startTime,
      problem_focus_coping: startTime,
      media_regulation: startTime
    });

    try {
      // 挨个生成建议，避免竞态条件
      const newSuggestions: Record<string, any> = {};
      const newGenerationOrder: string[] = [];

      for (let i = 0; i < suggestionTypes.length; i++) {
        const type = suggestionTypes[i];
        
        try {
          console.log(`[handleGenerateAllSuggestions] Generating ${type} suggestion...`);
          const result = await generateSuggestion(currentImageId, type);
          
          if (result) {
            newSuggestions[type] = result;
            newGenerationOrder.push(type);
            
            // 立即更新状态，让用户看到每个建议的生成进度
            setSuggestions(prev => ({
              ...prev,
              [type]: result
            }));
            
            console.log(`[handleGenerateAllSuggestions] Successfully generated ${type} suggestion`);
          }
    } catch (error) {
          console.error(`[handleGenerateAllSuggestions] Error generating ${type} suggestion:`, error);
          // 继续生成其他建议，不因为一个失败而停止
        }
      }

      // 更新生成顺序
      setSuggestionGenerationOrder(newGenerationOrder);

      // 保存建议到localStorage
      const savedSuggestions = JSON.parse(localStorage.getItem('savedSuggestions') || '{}');
      Object.assign(savedSuggestions, newSuggestions);
      localStorage.setItem('savedSuggestions', JSON.stringify(savedSuggestions));

      console.log('[handleGenerateAllSuggestions] Generated all suggestions:', newSuggestions);
    } catch (error) {
      console.error('[handleGenerateAllSuggestions] Error generating suggestions:', error);
      setSuggestionError(t('complete.suggestions.error'));
    } finally {
      // 清除所有加载状态
      setLoadingSuggestions({
        cognitive_reappraisal: false,
        problem_focus_coping: false,
        media_regulation: false
      });
      // 清除时间状态
      setSuggestionStartTimes({});
      setCurrentSuggestionTimes({});
    }
  };



    // 新增：重新分析函数
  // 换张截图功能
  const handleTryNewImage = async () => {
    try {
      // 1. 删除当前图片
      if (currentImageId) {
        await deleteImage(currentImageId);
      }
      
      // 2. 重置所有状态
      reset();
      setAnxietyLevel(0);
      setImageUrl(null);
      setImageFileName(null);
      setCachedImageFile(null);
      setCurrentImageId('');
      setCurrentStep('initial');
      setIsImageAnonymized(false); // 重置匿名化状态
      
      // 3. 滚动到分析部分标题
      const analysisSection = document.getElementById('analysis-section');
      if (analysisSection) {
        analysisSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // 如果找不到元素，回退到页面顶部
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      
    } catch (error) {
      console.error('[handleTryNewImage] Failed to delete image:', error);
      // 即使删除失败，也重置前端状态
      reset();
      setAnxietyLevel(0);
      setImageUrl(null);
      setImageFileName(null);
      setCachedImageFile(null);
      setCurrentImageId('');
      setCurrentStep('initial');
      setIsImageAnonymized(false); // 重置匿名化状态
      
      // 滚动到分析部分标题
      const analysisSection = document.getElementById('analysis-section');
      if (analysisSection) {
        analysisSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // 如果找不到元素，回退到页面顶部
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleReanalyze = async () => {
    if (!currentImageId) {
      console.error('[handleReanalyze] No current image ID');
      return;
    }

    if (!cachedImageFile) {
      console.error('[handleReanalyze] No cached image file available for reanalysis');
              setError(t('errors.noCachedImage'));
      return;
    }

    setIsReanalyzing(true);
    setAnalyzing(true);
    setAnalysisStartTime(Date.now());
    setIsAnonymizationReview(false);
    setPendingImageData(null);
    setIsWaitingForTriggers(false);
    
    try {
      // 1. 立即清除前端状态，让用户看到列表被清空
      console.log('[handleReanalyze] Clearing frontend state immediately');
      setTriggers([]);
      setTriggerStates({});
      setEditingTriggers({});
      setContactSubmitted(false);
      setContactSubmitting(false);
      
      // 2. 删除当前图片
      console.log('[handleReanalyze] Deleting current image:', currentImageId);
      await deleteImage(currentImageId);
      
      // 3. 使用 imageApi 重新上传缓存的图片文件
      console.log('[handleReanalyze] Re-uploading cached image file');
      const uploadResult = await uploadImage(cachedImageFile);
      const newImageId = uploadResult.image_id;
      
      console.log('[handleReanalyze] Image re-uploaded successfully, new ID:', newImageId);
      setCurrentImageId(newImageId);

      const initialImageData = await fetchImage(newImageId, true);
      const imageUrlBaseline: ImageUrlBaseline = {
        initialS3Location: initialImageData.s3_location,
        initialUrl: initialImageData.url,
      };
      console.log('[handleReanalyze] Initial image snapshot for anonymization polling:', imageUrlBaseline);

      // 4. 轮询等待匿名化处理完成，且 presigned url 已指向匿名化后的图片
      const anonymizedImageData = await pollForAnonymization(newImageId, imageUrlBaseline);
      console.log('[handleReanalyze] Anonymization poll complete:', anonymizedImageData);
      showAnonymizationReview(anonymizedImageData, imageUrlBaseline);
      
    } catch (error) {
      console.error('[handleReanalyze] Failed to reanalyze:', error);
      setError(typeof error === 'string' ? error : (error as Error).message || 'Reanalysis failed');
    } finally {
      setIsReanalyzing(false);
      setAnalyzing(false);
    }
  };

  // 新增：处理评价提交的函数
  const handleEvaluationSubmit = async () => {
    if (!currentImageId) {
      setEvaluationError(t('complete.evaluation.error'));
      return;
    }

    // 验证必填项
    if (evaluationData.relatabilityAfter === 0 || 
        evaluationData.helpfulnessAfter === 0 || 
        evaluationData.learnabilityAfter === 0) {
      setEvaluationError(t('errors.evaluationRequired'));
      return;
    }

    // 验证建议评分必填项
    const suggestionTypes = ['cognitive_reappraisal', 'problem_focus_coping', 'media_regulation'];
    const missingSuggestionRatings = suggestionTypes.filter(type => !suggestionRatings[type]);
    
    if (missingSuggestionRatings.length > 0) {
      setEvaluationError(t('errors.suggestionRatingRequired'));
      return;
    }

    setIsSubmittingEvaluation(true);
    setEvaluationError(null);

    try {
      const feedbackData = {
        emo_intensity_after: Number(evaluationData.emoIntensityAfter),
        relatability_after: Number(evaluationData.relatabilityAfter) * 2, // 将1-5转换为2-10
        helpfulness_after: Number(evaluationData.helpfulnessAfter) * 2, // 将1-5转换为2-10
        learnability_after: Number(evaluationData.learnabilityAfter) * 2, // 将1-5转换为2-10
        comment_after: (() => {
          // 将联系方式信息和访谈参与信息合并到comment中
          const comments = [];
          if (evaluationData.comment) {
            comments.push(evaluationData.comment);
          }
          if (evaluationData.contactInfo) {
            comments.push(`[抽奖联系方式]: ${evaluationData.contactInfo}`);
          }
          if (interviewParticipation && interviewContactInfo) {
            comments.push(`[访谈参与联系方式]: ${interviewContactInfo}`);
          }
          return comments.length > 0 ? comments.join('\n\n') : undefined;
        })(),
        best_suggestion: evaluationData.mostEffectiveSuggestion || undefined
      };

      console.log('[handleEvaluationSubmit] Sending feedback data:', feedbackData);
      await updateImageInfoAfter(currentImageId, feedbackData);
      
      setEvaluationSubmitted(true);
      console.log('[handleEvaluationSubmit] Evaluation submitted successfully');
      
      // 延迟滚动到参与活动组件，确保DOM更新完成
      setTimeout(() => {
        const participateActivitiesSection = document.getElementById('participate-activities-section');
        if (participateActivitiesSection) {
          participateActivitiesSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    } catch (error) {
      console.error('[handleEvaluationSubmit] Error submitting evaluation:', error);
      setEvaluationError(t('complete.evaluation.error'));
    } finally {
      setIsSubmittingEvaluation(false);
    }
  };

  // GAD-7 相关处理函数
  const handleGad7AnswerChange = (questionKey: string, value: number) => {
    setGad7Answers(prev => ({
      ...prev,
      [questionKey]: value
    }));
  };

  const calculateGad7Score = async () => {
    const questions = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7'];
    let total = 0;
    let allAnswered = true;

    console.log('GAD-7 Answers:', gad7Answers);

    questions.forEach(q => {
      if (gad7Answers[q] !== undefined && gad7Answers[q] !== null) {
        total += gad7Answers[q];
          } else {
        allAnswered = false;
          }
        });

    console.log('All answered:', allAnswered, 'Total score:', total);

    if (allAnswered) {
      setGad7Score(total);
      setShowGad7Result(true);
      console.log('Setting GAD-7 result - Score:', total, 'Show result:', true);
      
      // 调用API保存GAD-7分数
      try {
        const response = await createUserGad7(total);
        console.log('[calculateGad7Score] GAD-7 score saved successfully:', response);
      } catch (error) {
        console.error('[calculateGad7Score] Failed to save GAD-7 score:', error);
        // 不阻止用户看到结果，只记录错误
      }
    } else {
              alert(t('errors.gad7Incomplete'));
    }
  };

  const getGad7Level = (score: number) => {
    if (score <= 4) return 'mild';
    if (score <= 9) return 'light';
    if (score <= 14) return 'moderate';
    return 'severe';
  };



  // 重置建议相关状态
  const resetSuggestions = () => {
    setSuggestions({});
    setLoadingSuggestions({
      cognitive_reappraisal: false,
      problem_focus_coping: false,
      media_regulation: false
    });
    setExpandedSuggestions({});
    setSuggestionRatings({});
    setConfirmedAccurateResults([]);
    setSuggestionGenerationOrder([]);

    setContactSubmitted(false);
    setContactSubmitting(false);
    
    // 清空localStorage中的建议
    localStorage.removeItem('savedSuggestions');
  };

  // 处理hero区域开始分析按钮点击
  const handleHeroStartAnalysis = () => {
    // 如果已经有图片上传，显示确认对话框
    if (imageUrl) {
      setShowNewAnalysisConfirm(true);
    } else {
      // 没有图片，直接滚动到分析区域
      document.getElementById('analysis-section')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // 确认开始新分析
  const handleConfirmNewAnalysis = () => {
    setShowNewAnalysisConfirm(false);
    // 清空当前数据
    setImageUrl('');
    setImageFileName(null);
    setCachedImageFile(null);
    setTriggers([]);
    setCurrentStep('initial');
    setAnalyzing(false);
    setAnxietyLevel(0);
    setFeedbackAccuracy(false);
    setFeedbackClarity(false);
    setFeedbackNovelty(false);
    setFeedbackNoveltyDetail('');
    setTriggerStates({});
    setEditingTriggers({});
    setError('');
    resetSuggestions();
    // 滚动到分析区域
    document.getElementById('analysis-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  // 处理添加新trigger
  const handleAddTrigger = async () => {
    if (newTriggerTitle.trim() && newTriggerExplanation.trim()) {
      try {
        // 获取当前imageId
        const currentImageId = useAnalysisStore.getState().currentImageId;
        
        if (!currentImageId) {
          console.error('No current image ID available for creating custom trigger');
          return;
        }

        // 调用后端API创建自定义trigger
        const customTriggerData = {
          image_id: currentImageId,
          title: newTriggerTitle.trim(),
          explanation: newTriggerExplanation.trim(),
          type: 'custom' // 可选字段，标识为自定义trigger
        };

        const createdTrigger = await createCustomTrigger(customTriggerData);
        
        // 创建本地trigger对象，保持与现有trigger格式兼容
        const newTrigger: Trigger = {
          id: createdTrigger.trigger_id, // 使用后端返回的trigger_id
          trigger_id: createdTrigger.trigger_id, // 保存真实的trigger_id
          title: createdTrigger.title,
          explanation: createdTrigger.explanation,
          impact: 'medium',
          rating: null,
          isCustom: true // 标识为自定义trigger
        };
        
        // 添加到triggers列表
        const currentTriggers = useAnalysisStore.getState().triggers;
        setTriggers([...currentTriggers, newTrigger]);
        
        // 设置新trigger的状态为已接受
        setTriggerStates(prev => ({
          ...prev,
          [newTrigger.id]: {
            isAccepted: true,
            rating: null
          }
        }));
        
        // 清空表单
        setNewTriggerTitle('');
        setNewTriggerExplanation('');
        setShowAddTriggerForm(false);
        
        console.log('Custom trigger created successfully:', createdTrigger);
      } catch (error) {
        console.error('Failed to create custom trigger:', error);
        // 可以在这里添加错误提示给用户
      }
    }
  };

  // 取消添加trigger
  const handleCancelAddTrigger = () => {
    setNewTriggerTitle('');
    setNewTriggerExplanation('');
    setShowAddTriggerForm(false);
  };

  // GAD-7 问答导航函数
  const handleGad7Next = () => {
    if (currentGad7Question < 6) {
      setCurrentGad7Question(currentGad7Question + 1);
    }
  };

  const handleGad7Prev = () => {
    if (currentGad7Question > 0) {
      setCurrentGad7Question(currentGad7Question - 1);
    }
  };

  const handleGad7Complete = () => {
    // 检查是否所有问题都已回答
    const allAnswered = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7'].every(q => 
      gad7Answers[q] !== undefined && gad7Answers[q] !== null
    );
    
    if (allAnswered) {
      calculateGad7Score();
    }
  };

  // 处理联系方式提交
  const handleContactSubmit = async () => {
    // 检查是否有至少一个联系方式
    const hasRaffleContact = evaluationData.contactInfo.trim() !== '';
    const hasInterviewContact = interviewParticipation && interviewContactInfo.trim() !== '';
    
    if (!hasRaffleContact && !hasInterviewContact) {
              alert(t('errors.contactRequired'));
      return;
    }

    setContactSubmitting(true);
    
    try {
      const contactData: any = {};
      
      if (hasRaffleContact) {
        contactData.raffle_contact = evaluationData.contactInfo.trim();
      }
      
      if (hasInterviewContact) {
        contactData.interview_contact = interviewContactInfo.trim();
      }

      const response = await createUserContact(contactData);
      console.log('[handleContactSubmit] Contact information saved successfully:', response);
      
      setContactSubmitted(true);
      
      // 保存到localStorage以便下次自动填充
      const contactInfo = {
        raffle_contact: hasRaffleContact ? evaluationData.contactInfo.trim() : '',
        interview_contact: hasInterviewContact ? interviewContactInfo.trim() : '',
        interview_participation: interviewParticipation
      };
      localStorage.setItem('userContactInfo', JSON.stringify(contactInfo));
      
    } catch (error) {
      console.error('[handleContactSubmit] Failed to save contact information:', error);
              alert(t('errors.contactSaveFailed'));
    } finally {
      setContactSubmitting(false);
    }
  };

  // 处理"同上"按钮点击，自动填充抽奖联系方式
  const handleSameAsRaffle = () => {
    if (evaluationData.contactInfo.trim()) {
      updateInterviewContactInfo(evaluationData.contactInfo.trim());
    } else {
              alert(t('errors.raffleContactRequired'));
    }
  };

  // 检查localStorage中是否已有联系方式
  const hasExistingContactInfo = () => {
    const savedContactInfo = localStorage.getItem('userContactInfo');
    if (savedContactInfo) {
      try {
        const parsedContactInfo = JSON.parse(savedContactInfo);
        return parsedContactInfo.raffle_contact || parsedContactInfo.interview_contact;
      } catch (e) {
        return false;
      }
    }
    return false;
  };

  // 当triggers更新时，同步更新triggerStates
  useEffect(() => {
    if (triggers.length > 0) {
      setTriggerStates(prevStates => {
        const newTriggerStates: Record<string, { isAccepted: boolean; rating: number | null }> = {};
        triggers.forEach(trigger => {
          // 如果已经有状态，保持现有状态；否则设置默认状态
          newTriggerStates[trigger.id] = prevStates[trigger.id] || {
            isAccepted: false, // 默认为未接受状态
            rating: null
          };
        });
        return newTriggerStates;
      });
    }
  }, [triggers]); // 只依赖于triggers数组

  // 新增：控制登录组件显示
  const [showLogin, setShowLogin] = useState(false);

  const gad7Ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if ((analyzing || isReanalyzing) && gad7Ref.current) {
      setTimeout(() => {
        gad7Ref.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
      }, 500); // 延迟500ms，让用户先看到分析开始
    }
  }, [analyzing, isReanalyzing]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 via-white to-white">
      {/* 顶部导航栏 */}
      <nav className="sticky top-0 bg-white/80 backdrop-blur-md border-b border-gray-100 z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-8">
              <div className="flex items-center space-x-3">
                <svg className="w-6 h-6 text-teal-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="11" cy="11" r="8" strokeWidth="2"/>
                  <path d="m21 21-4.35-4.35" strokeWidth="2"/>
                  <circle cx="11" cy="11" r="3" strokeWidth="2" fill="currentColor" fillOpacity="0.2"/>
                  <path d="M8 11h6M11 8v6" strokeWidth="1.5" stroke="white"/>
                </svg>
                <h1 className="text-lg font-semibold text-gray-800">{t('app.title')}</h1>
              </div>
              
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  const newLang = language === 'zh' ? 'en' : 'zh';
                  setLanguage(newLang);
                  setHasUserManuallyChangedLanguage(true);
                  
                  // 异步更新后端用户信息，不阻塞UI更新
                  if (isLoggedIn && userInfo) {
                    const updateUserLanguage = async () => {
                      try {
                        const userInfoRequest: UserInfoRequest = {
                          gender: userInfo.gender || '',
                          age_range: userInfo.age_range || '',
                          occupation: userInfo.occupation || '',
                          language: newLang,
                          other_info: userInfo.other_info || ''
                        };
                        
                        await createUserInfo(userInfoRequest);
                        console.log('[Language Switch] Updated user language preference to:', newLang);
                        
                        // 更新本地缓存的用户信息
                        const updatedUserInfo = { ...userInfo, language: newLang };
                        localStorage.setItem('cachedUserInfo', JSON.stringify(updatedUserInfo));
                      } catch (error) {
                        console.error('[Language Switch] Failed to update user language preference:', error);
                      }
                    };
                    
                    // 立即执行异步操作，不等待结果
                    updateUserLanguage();
                  }
                }}
                className="flex items-center space-x-1 text-gray-600 hover:text-teal-600 transition-colors"
                title={language === 'zh' ? t('common.switchToEnglish') : t('common.switchToChinese')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
                <span className="text-sm font-medium">{language === 'zh' ? 'EN' : '中'}</span>
              </button>
              {isLoggedIn && (
                <>
                  <div className="hidden sm:flex items-center space-x-2 text-sm text-gray-600">
                    {/* Quota余额显示 */}
                    {userInfo && typeof userInfo.quota === 'number' && (
                      <div className="flex items-center space-x-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <span className={`font-medium ${
                          userInfo.quota === 0 && !analyzing && !isLoading ? 'text-red-600' : 
                          userInfo.quota <= 3 ? 'text-yellow-600' : 
                          'text-teal-600'
                        }`}>
                          {userInfo.quota}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* 移动端简化显示 */}
                  <div className="flex sm:hidden items-center text-sm">
                    {userInfo && typeof userInfo.quota === 'number' && (
                      <div className="flex items-center space-x-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <span className={`font-medium ${
                          userInfo.quota === 0 && !analyzing && !isLoading ? 'text-red-600' : 
                          userInfo.quota <= 3 ? 'text-yellow-600' : 
                          'text-teal-600'
                        }`}>
                          {userInfo.quota}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={() => {
                      logout();
                      // 清除缓存的图片文件
                      setCachedImageFile(null);
                    }}
                    className="text-gray-600 hover:text-red-600 transition-colors text-sm"
                  >
                    {t('navigation.logout')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* 主要内容区域 */}
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        {!isLoggedIn ? (
          // 未登录时显示登录界面
          <>
            {/* 英雄区域 - 登录前 */}
            <section className="py-8 md:py-12">
              <div className="max-w-3xl mx-auto text-center">
                <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-6 leading-tight">
                  {t('hero.titleLoggedOut')}
            </h1>
                <div className="space-y-4 mb-6">
                  <p className="text-xl md:text-2xl text-gray-700 font-medium leading-relaxed">
                    {t('hero.subtitle1LoggedOut')}
                  </p>
                  <p className="text-base text-teal-800 leading-relaxed">
                    {t('hero.subtitle2LoggedOut')}
                </p>
                </div>
              </div>
            </section>

            {/* 用户须知 */}
            <section className="py-0">
              <div className="max-w-4xl mx-auto px-1">
                <div className="bg-white rounded-lg p-6">
                  <h2 className="text-xl text-center font-semibold text-gray-800 mb-4">
                    {t('userNotice.title')}
                  </h2>
                  <div className="space-y-6">
                    {/* 工具介绍 */}
                    <div>
                      <p className="text-gray-700 leading-relaxed">
                        {t('userNotice.introduction')}
                      </p>
                </div>

                    {/* 使用流程 */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-800 mb-3">
                        {t('userNotice.process.title')}
                      </h3>
                      <div className="space-y-4">
                        {/* 步骤1 */}
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                    </div>
                          <span className="text-gray-700">{t('userNotice.process.step1')}</span>
                      </div>
                        
                        {/* 步骤2 */}
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                    </div>
                          <span className="text-gray-700">{t('userNotice.process.step2')}</span>
                      </div>
                        
                        {/* 步骤3 */}
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                    </div>
                          <span className="text-gray-700">{t('userNotice.process.step3')}</span>
                      </div>
                        
                        {/* 步骤4 */}
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                    </div>
                          <span className="text-gray-700">{t('userNotice.process.step4')}</span>
                  </div>
                </div>
                    </div>

                    {/* 数据用途与隐私保护 */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-800 mb-3">
                        {t('userNotice.privacy.title')}
                      </h3>
                      <ul className="space-y-2 text-gray-700 leading-relaxed">
                        <li className="flex items-start">
                          <span className="font-medium mr-2">•</span>
                          <span>{t('userNotice.privacy.item1')}</span>
                        </li>
                        <li className="flex items-start">
                          <span className="font-medium mr-2">•</span>
                          <span>{t('userNotice.privacy.item2')}</span>
                        </li>
                        <li className="flex items-start">
                          <span className="font-medium mr-2">•</span>
                          <span>{t('userNotice.privacy.item3')}</span>
                        </li>
                        <li className="flex items-start">
                          <span className="font-medium mr-2">•</span>
                          <span>{t('userNotice.privacy.item4')}</span>
                        </li>
                      </ul>
                  </div>

                    {/* 同意声明 */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h3 className="text-lg font-medium text-gray-800 mb-3">
                        {t('userNotice.consent.title')}
                      </h3>
                      <ul className="space-y-2 text-gray-700 leading-relaxed">
                        <li className="flex items-start">
                          <span className="font-medium mr-2">•</span>
                          <span>{t('userNotice.consent.item1')}</span>
                        </li>
                        <li className="flex items-start">
                          <span className="font-medium mr-2">•</span>
                          <span>{t('userNotice.consent.item2')}</span>
                        </li>
                        <li className="flex items-start">
                          <span className="font-medium mr-2">•</span>
                          <span>{t('userNotice.consent.item3')}</span>
                        </li>
                        <li className="flex items-start">
                          <span className="font-medium mr-2">•</span>
                          <span>{t('userNotice.consent.item4')}</span>
                        </li>
                      </ul>
                      <div className="mt-6 text-center">
                        <button
                          className="inline-block px-8 py-3 bg-teal-600 text-white rounded-lg font-semibold text-lg shadow hover:bg-teal-700 transition-colors"
                          onClick={() => {
                            setShowLogin(true);
                            // 滚动到页面底部，让用户看到登录区域
                            setTimeout(() => {
                              window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                            }, 100);
                          }}
                          disabled={showLogin}
                        >
                          {t('userNotice.startButton')}
                        </button>
                  </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 登录组件 */}
            {showLogin && <LoginSection />}
          </>
        ) : (
          // 登录后显示分析功能
          <>
            {/* 英雄区域 - 登录后 */}
            <section className="py-6 md:py-8">
              <div className="max-w-3xl mx-auto text-center">
                <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-6 leading-tight">
                  {t('hero.titleLoggedIn')}
                </h1>
                <div className="space-y-4 mb-6">
                  <p className="text-xl md:text-2xl text-gray-700 font-medium leading-relaxed">
                    {t('hero.subtitle1LoggedIn')}
                </p>
                  <p className="text-base text-teal-800 leading-relaxed">
                    {t('hero.subtitle2LoggedIn')}
                  </p>
                </div>
              <button
                onClick={handleHeroStartAnalysis}
                disabled={Boolean(userInfo && typeof userInfo.quota === 'number' && userInfo.quota <= 0 && !analyzing && !isLoading)}
                className={`px-8 py-4 rounded-lg text-white transition-all duration-300 font-medium shadow-lg transform ${
                  Boolean(userInfo && typeof userInfo.quota === 'number' && userInfo.quota <= 0 && !analyzing && !isLoading)
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 hover:shadow-xl hover:-translate-y-0.5'
                }`}
              >
                {t('hero.startButton')}
              </button>
              
              {/* Quota余额提示 */}
              {userInfo && typeof userInfo.quota === 'number' && (
                <div className="mt-3 text-center">
                  <p className={`text-sm font-medium ${
                    userInfo.quota === 0 && !analyzing && !isLoading ? 'text-red-600' : 
                    userInfo.quota <= 3 ? 'text-yellow-600' : 
                    'text-gray-600'
                  }`}>
                    {language === 'zh' 
                      ? `剩余分析次数：${userInfo.quota} 次` 
                      : `Remaining analyses: ${userInfo.quota}`
                    }
                  </p>
                </div>
              )}
              
              {/* 余额不足提示 */}
              {userInfo && typeof userInfo.quota === 'number' && userInfo.quota <= 0 && !analyzing && !isLoading && (
                <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <p className="text-sm text-orange-700 font-medium">
                      {language === 'zh' ? '本邀请码分析次数已用完，继续体验请联系研究员' : 'Analysis quota exhausted. Please contact administrator for more quota.'}
                    </p>
                  </div>
                </div>
              )}
          </div>
        </section>

        {/* 工作原理 - 可折叠版 */}
            <section id="how-it-works" className="py-4 border-gray-100">
              <div className="max-w-4xl mx-auto px-2">
                {/* 可点击的标题头部 */}
                <button
                  onClick={() => setIsHowItWorksExpanded(!isHowItWorksExpanded)}
                  className="w-full flex items-center justify-center space-x-1 text-base font-medium text-gray-700 mb-3 transition-colors focus:outline-none"
                >
                  <span>{t('howItWorks.title')}</span>
                  <svg 
                    className={`w-4 h-4 transition-transform duration-200 ${isHowItWorksExpanded ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* 折叠内容区域 */}
                {isHowItWorksExpanded && (
                  <div className="transition-all duration-300 ease-in-out">
                    {/* 移动端垂直布局 - 紧凑版 */}
                <div className="md:hidden">
                  <div className="space-y-6">
                    {/* 步骤1 */}
                    <div className="flex items-start space-x-3 bg-white rounded-lg shadow-sm border border-gray-100 p-3 relative">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold mb-1">
                        {t('howItWorks.step1.title')}
                      </h3>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {t('howItWorks.step1.description')}
                      </p>
                      </div>
                      {/* 下箭头 */}
                      <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-white">
                        <svg className="w-4 h-4 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                      </div>
                    </div>
                    
                    {/* 步骤2 */}
                    <div className="flex items-start space-x-3 bg-white rounded-lg shadow-sm border border-gray-100 p-3 relative">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold mb-1">
                        {t('howItWorks.step2.title')}
                      </h3>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {t('howItWorks.step2.description')}
                      </p>
                      </div>
                      {/* 下箭头 */}
                      <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-white">
                        <svg className="w-4 h-4 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                      </div>
                    </div>
                    
                    {/* 步骤3 */}
                    <div className="flex items-start space-x-3 bg-white rounded-lg shadow-sm border border-gray-100 p-3 relative">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold mb-1">
                        {t('howItWorks.step3.title')}
                      </h3>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {t('howItWorks.step3.description')}
                      </p>
                      </div>
                      {/* 下箭头 */}
                      <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-white">
                        <svg className="w-4 h-4 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                      </div>
                    </div>
                    
                    {/* 步骤4 */}
                    <div className="flex items-start space-x-3 bg-white rounded-lg shadow-sm border border-gray-100 p-3">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold mb-1">
                        {t('howItWorks.step4.title')}
                      </h3>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {t('howItWorks.step4.description')}
                      </p>
                    </div>
                    </div>
                  </div>
                </div>
                
                {/* 桌面端网格布局 - 紧凑版 */}
                <div className="hidden md:flex items-start justify-center space-x-6">
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-2">
                  <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">
                      {t('howItWorks.step1.title')}
                </h3>
                <p className="text-sm text-gray-600">
                      {t('howItWorks.step1.description')}
                </p>
              </div>
              {/* 箭头 */}
              <div className="flex-shrink-0 pt-16">
                <svg className="w-8 h-8 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-2">
                  <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">
                      {t('howItWorks.step2.title')}
                </h3>
                <p className="text-sm text-gray-600">
                      {t('howItWorks.step2.description')}
                </p>
              </div>
              {/* 箭头 */}
              <div className="flex-shrink-0 pt-16">
                <svg className="w-8 h-8 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-2">
                  <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">
                      {t('howItWorks.step3.title')}
                </h3>
                <p className="text-sm text-gray-600">
                      {t('howItWorks.step3.description')}
                </p>
              </div>
              {/* 箭头 */}
              <div className="flex-shrink-0 pt-16">
                <svg className="w-8 h-8 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
                  <div className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-2">
                      <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold mb-2">
                      {t('howItWorks.step4.title')}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {t('howItWorks.step4.description')}
                    </p>
                  </div>
            </div>
                  </div>
                )}
                
                {/* 收起按钮 */}
                {isHowItWorksExpanded && (
                  <div className="flex justify-center mt-4">
                    <button
                      onClick={() => setIsHowItWorksExpanded(false)}
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:text-teal-600 transition-colors rounded-lg hover:bg-gray-50"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                      </svg>
                      <span>收起</span>
                    </button>
                  </div>
                )}
          </div>
        </section>

        {/* 分析部分 */}
            <section id="analysis-section" className="py-4 border-t border-gray-100">
          <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  {t('analysis.title')}
            </h2>

            {/* 分析表单 */}
                <div className="p-6">
              {error && (
                <div className="bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-lg mb-6">
                  {error}
                </div>
              )}

              {currentStep === 'initial' && (
                    <div className="space-y-6">
                      {/* 简化的使用说明 */}

                      {/* 用户身份信息 - 简化版 */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-medium text-gray-800">
                          {userInfo ? t('analysis.basicInfo.displayTitle') : t('analysis.basicInfo.title')}
                        </h3>
                    
                        {isUserInfoLoading ? (
                          <div className="text-center py-6 text-gray-500">
                            {t('analysis.basicInfo.loading')}
                          </div>
                        ) : userInfo ? (
                          // 展示已有的用户信息 - 简化版
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                                <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-800">
                                  {[
                                    userInfo.gender && t(`analysis.basicInfo.gender.${userInfo.gender}`),
                                    userInfo.age_range && t(`analysis.basicInfo.age.${userInfo.age_range}`),
                                    userInfo.occupation && t(`analysis.basicInfo.occupation.${userInfo.occupation}`)
                                  ].filter(Boolean).join(' • ')}
                              </p>
                              {/* @ts-ignore */}
                              {userInfo.other_info?.additional_info && (
                                  <p className="text-xs text-gray-600 mt-1 truncate max-w-xs">
                                  {/* @ts-ignore */}
                                  {userInfo.other_info.additional_info}
                                </p>
                              )}
                            </div>
                            </div>
                              <button 
                                onClick={() => setUserInfo(null)}
                              className="text-xs text-teal-600 hover:text-teal-800 transition-colors flex items-center gap-1"
                              >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L16.732 3.732z" />
                                </svg>
                                {t('analysis.basicInfo.editButton')}
                              </button>
                          </div>
                        ) : (
                                                      // 显示信息填写表单
                            <>
                           
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {/* 性别 */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('analysis.basicInfo.gender.label')} <span className="text-red-500">*</span>
                                </label>
                                      <div className="flex gap-2">
                                  {['male', 'female', 'other', 'preferNotToSay'].map((option) => (
                                          <label key={option} className="flex items-center">
                                      <input
                                        type="radio"
                                        name="gender"
                                        value={option}
                                        checked={userIdentity.gender === option}
                                        onChange={(e) => updateUserIdentity(prev => ({ ...prev, gender: e.target.value }))}
                                              className="mr-1 text-teal-600 focus:ring-teal-500 bg-white !bg-white dark:!bg-white"
                                      />
                                            <span className="text-sm">
                                              {t(`analysis.basicInfo.gender.${option}`)}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                                {userInfoSubmitMessage === t('analysis.userInfoRequired') && !userIdentity.gender && (
                                  <p className="text-red-500 text-xs mt-1">请选择性别或选择"不愿透露"</p>
                                )}
                              </div>

                              {/* 年龄段 */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('analysis.basicInfo.age.label')} <span className="text-red-500">*</span>
                                </label>
                                      <select
                                        value={userIdentity.ageGroup}
                                        onChange={(e) => updateUserIdentity(prev => ({ ...prev, ageGroup: e.target.value }))}
                                        className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm bg-white ${
                                          userInfoSubmitMessage === t('analysis.userInfoRequired') && !userIdentity.ageGroup 
                                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                                            : 'border-gray-200'
                                        }`}
                                      >
                                        <option value="">{t('analysis.basicInfo.age.placeholder')}</option>
                                        <option value="18-24">{t('analysis.basicInfo.age.18-24')}</option>
                                        <option value="25-30">{t('analysis.basicInfo.age.25-30')}</option>
                                        <option value="31-35">{t('analysis.basicInfo.age.31-35')}</option>
                                        <option value="36-40">{t('analysis.basicInfo.age.36-40')}</option>
                                        <option value="41-50">{t('analysis.basicInfo.age.41-50')}</option>
                                        <option value="51-60">{t('analysis.basicInfo.age.51-60')}</option>
                                        <option value="61-70">{t('analysis.basicInfo.age.61-70')}</option>
                                        <option value="71-80">{t('analysis.basicInfo.age.71-80')}</option>
                                        <option value="80plus">{t('analysis.basicInfo.age.80plus')}</option>
                                        <option value="preferNotToSay">{t('analysis.basicInfo.age.preferNotToSay')}</option>
                                      </select>
                                      {userInfoSubmitMessage === t('analysis.userInfoRequired') && !userIdentity.ageGroup && (
                                        <p className="text-red-500 text-xs mt-1">请选择年龄段或选择"不愿透露"</p>
                                      )}
                              </div>

                              {/* 职业状态 */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('analysis.basicInfo.occupation.label')} <span className="text-red-500">*</span>
                                </label>
                                      <select
                                        value={userIdentity.occupation}
                                        onChange={(e) => updateUserIdentity(prev => ({ ...prev, occupation: e.target.value }))}
                                        className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm bg-white ${
                                          userInfoSubmitMessage === t('analysis.userInfoRequired') && !userIdentity.occupation 
                                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                                            : 'border-gray-200'
                                        }`}
                                      >
                                        <option value="">{t('analysis.basicInfo.occupation.placeholder')}</option>
                                        <option value="collegeStudent">{t('analysis.basicInfo.occupation.collegeStudent')}</option>
                                        <option value="undergraduate">{t('analysis.basicInfo.occupation.undergraduate')}</option>
                                        <option value="masterStudent">{t('analysis.basicInfo.occupation.masterStudent')}</option>
                                        <option value="phdStudent">{t('analysis.basicInfo.occupation.phdStudent')}</option>
                                        <option value="workingFullTime">{t('analysis.basicInfo.occupation.workingFullTime')}</option>
                                        <option value="workingPartTime">{t('analysis.basicInfo.occupation.workingPartTime')}</option>
                                        <option value="freelancer">{t('analysis.basicInfo.occupation.freelancer')}</option>
                                        <option value="entrepreneur">{t('analysis.basicInfo.occupation.entrepreneur')}</option>
                                        <option value="unemployed">{t('analysis.basicInfo.occupation.unemployed')}</option>
                                        <option value="fullTimeParent">{t('analysis.basicInfo.occupation.fullTimeParent')}</option>
                                        <option value="retired">{t('analysis.basicInfo.occupation.retired')}</option>
                                        <option value="notInLaborForce">{t('analysis.basicInfo.occupation.notInLaborForce')}</option>
                                        <option value="other">{t('analysis.basicInfo.occupation.other')}</option>
                                        <option value="preferNotToSay">{t('analysis.basicInfo.occupation.preferNotToSay')}</option>
                                      </select>
                                      {userInfoSubmitMessage === t('analysis.userInfoRequired') && !userIdentity.occupation && (
                                        <p className="text-red-500 text-xs mt-1">请选择职业状态或选择"不愿透露"</p>
                                      )}
                                </div>
                              </div>

                              {/* 其他信息 */}
                              <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  {t('analysis.basicInfo.otherInfo.label')}
                                </label>
                                <textarea
                                  value={userIdentity.otherInfo}
                                  onChange={(e) => updateUserIdentity(prev => ({ ...prev, otherInfo: e.target.value }))}
                                  placeholder={t('analysis.basicInfo.otherInfo.placeholder')}
                                  className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm resize-none bg-white"
                                  rows={3}
                                  maxLength={300}
                                />
                              </div>

                              {/* 用户信息提交按钮 */}
                              <div className="mt-6 pt-4 border-t border-gray-200">
                                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                  <button
                                    onClick={handleUserInfoSubmit}
                                    disabled={isSubmittingUserInfo || !userIdentity.gender || !userIdentity.ageGroup || !userIdentity.occupation}
                                    className={`px-6 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 flex items-center space-x-2 ${
                                      isSubmittingUserInfo || !userIdentity.gender || !userIdentity.ageGroup || !userIdentity.occupation
                                        ? 'bg-gray-400 text-white cursor-not-allowed'
                                        : 'bg-teal-600 hover:bg-teal-700 text-white shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
                                    }`}
                                  >
                                    {isSubmittingUserInfo ? (
                                      <>
                                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        <span>{t('analysis.userInfoSaving')}</span>
                                      </>
                                    ) : (
                                      <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span>
                                          {t('analysis.submitUserInfo')}
                                        </span>
                                      </>
                                    )}
                                  </button>
                                  
                                  {/* 状态消息 */}
                                  {userInfoSubmitMessage && (
                                    <div className={`flex items-center space-x-2 text-sm font-medium ${
                                      userInfoSubmitMessage === t('analysis.userInfoSaved') 
                                        ? 'text-teal-600' 
                                        : 'text-red-600'
                                    }`}>
                                      {userInfoSubmitMessage === t('analysis.userInfoSaved') ? (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                        </svg>
                                      ) : (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      )}
                                      <span>{userInfoSubmitMessage}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                          </>
                        )}
                      </div>

                  {/* 表单 */}
                  {currentStep === 'initial' && isBasicInfoSaved && (
                    <form onSubmit={handlePostSubmit} className="space-y-4">
                      {/* 图片上传 - 精简版 */}
                      <div data-section="image-upload">
                        <label className="text-lg block font-medium text-gray-700 mb-2 flex items-center">
                          <span className="mr-2"></span>
                          {t('analysis.imageUpload.title')}
                        </label>
                        <div className="bg-teal-50 border border-teal-100 rounded-lg p-3 mb-3">
                          <div className="flex items-start space-x-2">
                            <svg className="w-3 h-3 text-teal-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-xs text-teal-700 leading-snug whitespace-pre-line">
                              {t('analysis.imageUpload.subtitle')}
                            </p>
                          </div>
                        </div>
                        <input
                          type="file"
                          ref={fileInputRef}
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                        <div
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                          onClick={() => fileInputRef.current?.click()}
                          className={`relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all duration-300 min-h-40 flex flex-col justify-center items-center ${
                            isDragging
                              ? 'border-teal-500 bg-teal-50 transform scale-105'
                              : 'border-gray-300 hover:border-teal-400 hover:bg-gray-50'
                          }`}
                        >
                          {imageUrl ? (
                            <>
                              <img
                                src={imageUrl}
                                alt="Preview"
                                width={120}
                                height={90}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleImagePreviewClick();
                                }}
                                className="mx-auto rounded-lg object-contain shadow-md cursor-pointer hover:opacity-75 transition-opacity"
                                title={t('analysis.imageUpload.clickToEnlarge') || '点击放大查看'}
                              />
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setImageUrl(null)
                                  setImageFileName(null);
                                }}
                                className="absolute top-2 right-2 bg-red-500 text-white w-6 h-6 rounded-full hover:bg-red-600 transition-colors text-xs shadow-lg flex items-center justify-center"
                              >
                                ✕
                              </button>
                            </>
                          ) : (
                            <div className="flex flex-col items-center gap-2">
                              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                                <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                              <div>
                                <p className="text-gray-700 text-sm font-medium mb-1">
                                  {t('analysis.imageUpload.dragText')}
                                </p>
                                <p className="text-gray-500 text-xs">
                                  {t('analysis.imageUpload.formatText')}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="mt-2 border border-gray-200 rounded-lg bg-gray-50 px-3 py-2">
                          <p className="text-center text-xs text-gray-500 mb-2">
                            {t('analysis.imageUpload.sampleImagesHint')}
                          </p>
                          <div className="flex justify-center gap-3">
                            {SAMPLE_IMAGES.map(({ src, labelKey }) => (
                              <div key={src} className="flex flex-col items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleSampleImageSelect(src)}
                                  aria-label={`${t('analysis.imageUpload.sampleImage')}: ${t(`analysis.imageUpload.${labelKey}`)}`}
                                  className="rounded border border-gray-200 overflow-hidden hover:border-teal-400 hover:ring-1 hover:ring-teal-300 transition-all focus:outline-none focus:ring-2 focus:ring-teal-500"
                                >
                                  <img
                                    src={src}
                                    alt=""
                                    className="w-10 h-10 object-cover"
                                  />
                                </button>
                                <span className="text-[10px] text-gray-500 leading-tight text-center">
                                  {t(`analysis.imageUpload.${labelKey}`)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* 简化的附加信息 - 只在有图片且未进入匿名化确认时显示 */}
                      {imageUrl && !isAnonymizationReview && (
                        <div>
                          <div className={`flex items-center space-x-2 text-sm ${anxietyLevel === 0 ? 'text-amber-600' : 'text-teal-600'}`}>
                            {anxietyLevel === 0 ? (
                              // 未评分时显示下箭头和琥珀色提示
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                              </svg>
                            ) : (
                              // 已评分时显示勾选图标和青色提示
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            )}
                            <p className="font-medium">
                              {anxietyLevel === 0 
                                ? t('analysis.imageUpload.ratingPrompt')
                                : t('analysis.imageUpload.successText')
                              }
                            </p>
                          </div>
                        </div>
                      )}



                      {/* 焦虑程度评分滑动条 - 只在有图片且未进入匿名化确认时显示 */}
                      {imageUrl && !isAnonymizationReview && (
                        <div className="pt-4 border-t border-gray-200">
                          <div className="space-y-3">
                            <div className="flex items-center space-x-2">
                              
                              <h4 className="text-sm font-medium text-gray-700">
                                {t('common.anxietyLevelTitle')}
                              </h4>
                            </div>
                            
                            <div className="space-y-2">
                              <div className="flex justify-between text-xs text-gray-500">
                                <span>{t('common.notAnxiousAtAll')}</span>
                                <span className="font-medium text-gray-700">
                                  {anxietyLevel}/10
                                </span>
                                <span>{t('common.extremelyAnxious')}</span>
                              </div>
                              
                              <div className="relative">
                                <input
                                  type="range"
                                  min="0"
                                  max="10"
                                  value={anxietyLevel}
                                  onChange={(e) => setAnxietyLevel(parseInt(e.target.value))}
                                  className="slider w-full h-2 bg-gradient-to-r from-green-200 via-yellow-200 to-red-200 rounded-lg appearance-none cursor-pointer"
                                />
                                <div className="flex justify-between text-xs text-gray-400 mt-1">
                                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                                    <span key={num} className={anxietyLevel === num ? 'text-gray-700 font-medium' : ''}>
                                      {num}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              
                             
                            </div>
                          </div>
                        </div>
                      )}

                      {isAnonymizationReview ? (
                        <div className="pt-4 border-t border-gray-200 space-y-4">
                          <div className="text-center space-y-2">
                            <h4 className="text-sm font-medium text-gray-800">
                              {t('anonymizationReview.title')}
                            </h4>
                            <p className="text-xs text-gray-600">
                              {t('anonymizationReview.hint')}
                            </p>
                          </div>
                          <div className="flex flex-col sm:flex-row gap-3">
                            <button
                              type="button"
                              onClick={handleVerifyAnonymization}
                              disabled={isWaitingForTriggers || isReanalyzing}
                              className={`flex-1 py-3 px-4 rounded-lg text-white font-medium transition-colors ${
                                isWaitingForTriggers || isReanalyzing
                                  ? 'bg-gray-400 cursor-not-allowed'
                                  : 'bg-teal-600 hover:bg-teal-700'
                              }`}
                            >
                              {isWaitingForTriggers ? (
                                <span className="inline-flex items-center justify-center">
                                  <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                  </svg>
                                  {t('anonymizationReview.waitingForAnalysis')}
                                </span>
                              ) : (
                                t('anonymizationReview.verify')
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={handleReanalyze}
                              disabled={isReanalyzing || isWaitingForTriggers}
                              className={`flex-1 py-3 px-4 rounded-lg font-medium border transition-colors ${
                                isReanalyzing || isWaitingForTriggers
                                  ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                                  : 'border-teal-600 text-teal-700 hover:bg-teal-50'
                              }`}
                            >
                              {isReanalyzing ? t('results.reanalyzing') : t('anonymizationReview.reanonymize')}
                            </button>
                          </div>
                        </div>
                      ) : (
                      /* 分析按钮 */
                      <div className="pt-4">
                        
                        <button
                          type="submit"
                          disabled={isLoading || analyzing || !imageUrl || anxietyLevel === 0 || Boolean(userInfo && typeof userInfo.quota === 'number' && userInfo.quota <= 0 && !analyzing && !isLoading)}
                          className={`w-full py-4 rounded-lg text-white font-medium flex items-center justify-center transition-all duration-300 text-lg ${
                            isLoading || analyzing || !imageUrl || anxietyLevel === 0 || Boolean(userInfo && typeof userInfo.quota === 'number' && userInfo.quota <= 0 && !analyzing && !isLoading)
                              ? 'bg-gray-400 cursor-not-allowed' 
                              : 'bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                          }`}
                        >
                          {isLoading || analyzing ? (
                            <>
                                <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                                {t('analysis.imageUpload.analyzing')}
                            </>
                          ) : (
                            <>
                                <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                              </svg>
                                {t('analysis.imageUpload.startButton')}
                            </>
                          )}
                        </button>
                        
                        {/* 历史记录按钮 - 只在用户还没有上传截图时显示 */}
                        {!imageUrl && (
                          <div className="mt-3 text-center">
                            <Link
                              to="/dashboard"
                              className="text-teal-600 hover:text-teal-700 underline font-medium text-sm"
                            >
                              {t('complete.evaluation.dataManagement.historyRecord')}
                            </Link>
                          </div>
                        )}
                        
                        {/* 余额不足提示 */}
                        {userInfo && typeof userInfo.quota === 'number' && userInfo.quota <= 0 && !analyzing && !isLoading && (
                          <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                            <div className="flex items-center space-x-2">
                              <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                              </svg>
                              <p className="text-sm text-orange-700 font-medium">
                                {t('errors.quotaExhausted')}
                              </p>
                            </div>
                          </div>
                        )}
                        
                        {/* 分析时长和等待时间提示 */}
                        <div className="mt-3 space-y-2">
                          {/* 正在分析时显示当前用时和预计等待时间 */}
                          {(analyzing || isLoading) && (
                            <div className="space-y-1">
                              {/* 当前分析时间 */}
                              {currentAnalysisTime && (
                                <div className="text-sm text-teal-700 text-center">
                                  {language === 'zh' 
                                    ? `分析中... ${Math.floor(currentAnalysisTime / 1000)}秒` 
                                    : `Analyzing... ${Math.floor(currentAnalysisTime / 1000)}s`
                                  }
                                </div>
                              )}
                              {/* 预计等待时间 */}
                              <div className="flex items-center justify-center space-x-2">
                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="text-sm text-gray-600">
                                  {averageAnalysisTime ? (
                                    language === 'zh' 
                                      ? `预计等待时间: ${Math.floor(averageAnalysisTime / 1000)}秒` 
                                      : `Expected wait time: ${Math.floor(averageAnalysisTime / 1000)}s`
                                  ) : (
                                    language === 'zh' 
                                      ? '预计等待时间: 约20秒' 
                                      : 'Expected wait time: ~20s'
                                  )}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* 未分析时只显示预计等待时间提示 */}
                          {!analyzing && !isLoading && imageUrl && (
                            <div className="flex items-center justify-center space-x-2">
                              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className="text-sm text-gray-600">
                                {averageAnalysisTime ? (
                                  language === 'zh' 
                                    ? `预计等待时间: ${Math.floor(averageAnalysisTime / 1000)}秒` 
                                    : `Expected wait time: ${Math.floor(averageAnalysisTime / 1000)}s`
                                ) : (
                                  t('common.expectedWaitTime')
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      )}
                    </form>
                  )}

                
                </div>
              )}

              {/* 分析结果 */}
              {currentStep === 'analysis' && (
                    <div className="mt-2">
                      {/* 上传的图片预览和用户信息展示 - 居中版 */}
                      <div className="rounded-lg p-2 space-y-2 mb-4">
                        <h3 className="text-lg font-medium text-gray-800 text-center">
                          {isImageAnonymized ? t('results.anonymizedImage') : t('results.uploadedImage')}
                        </h3>
                        
                        {/* 图片预览 - 居中版 */}
                        {imageUrl && (
                          <div className="flex flex-col items-center space-y-3">
                            <div className="relative">
                              <img
                                src={imageUrl}
                                alt="Analysis content"
                                width={120}
                                height={90}
                                onClick={handleImagePreviewClick}
                                className="rounded-lg object-contain border border-gray-200 shadow-sm cursor-pointer hover:opacity-75 transition-opacity"
                                title={t('analysis.imageUpload.clickToEnlarge') || '点击放大查看'}
                              />
                    </div>
                            
                          </div>
                        )}

                        {/* 用户信息展示 - 居中版 */}
                        {(userIdentity.gender || userIdentity.ageGroup || userIdentity.occupation) && (
                          <div className="flex items-center justify-center gap-2 text-xs text-gray-600">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span>
                              {userIdentity.gender && t(`analysis.basicInfo.gender.${userIdentity.gender}`)}
                              {userIdentity.gender && userIdentity.ageGroup && ' • '}
                              {userIdentity.ageGroup && t(`analysis.basicInfo.age.${userIdentity.ageGroup}`)}
                              {(userIdentity.gender || userIdentity.ageGroup) && userIdentity.occupation && ' • '}
                              {userIdentity.occupation && t(`analysis.basicInfo.occupation.${userIdentity.occupation}`)}
                                </span>
                    </div>
                        )}
                  </div>

                      {/* 标题区域 - 精简版 */}
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-gray-800" data-section="results-title">
                          {t('results.title')}
                        </h3>
                      </div>

                      {/* 使用指南 */}
                      <div className="bg-teal-50 border border-teal-100 rounded-lg p-3">
                        <div className="flex items-start space-x-2">
                          <svg className="w-3 h-3 text-teal-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-xs text-teal-700 leading-snug whitespace-pre-line">
                          {t('results.hint')}
                        </p>
                  </div>
                      </div>

                  {/* 分析时长和等待时间提示 - 重新分析时显示 */}
                  {(analyzing || isReanalyzing) && (
                    <div className="mt-3 space-y-2">
                      {/* 正在分析时显示当前用时和预计等待时间 */}
                      {(analyzing || isLoading) && (
                        <div className="space-y-1">
                          {/* 当前分析时间 */}
                          {currentAnalysisTime && (
                            <div className="text-sm text-teal-700 text-center">
                              {language === 'zh' 
                                ? `重新分析中... ${Math.floor(currentAnalysisTime / 1000)}秒` 
                                : `Reanalyzing... ${Math.floor(currentAnalysisTime / 1000)}s`
                              }
                            </div>
                          )}
                          {/* 预计等待时间 */}
                          <div className="flex items-center justify-center space-x-2">
                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                            <span className="text-sm text-gray-600">
                              {averageAnalysisTime ? (
                                language === 'zh' 
                                  ? `预计等待时间: ${Math.floor(averageAnalysisTime / 1000)}秒` 
                                  : `Expected wait time: ${Math.floor(averageAnalysisTime / 1000)}s`
                              ) : (
                                t('common.expectedWaitTime')
                              )}
                            </span>
                              </div>
                            </div>
                          )}
                        </div>
                  )}

                  {/* 触发因素列表 */}
                    <div className="space-y-4 mt-4">
                    {/* 显示所有触发因素，使用checkbox选择是否接受 */}
                    {triggers.map((trigger, _index) => {
                      // 防护：确保trigger.id存在，否则使用index作为fallback
                      const triggerId = trigger.id || `trigger-fallback-${_index}`;
                      const editState = editingTriggers[triggerId];
                      const isEditing = editState?.isEditing || false;
                      
                      return (
                        <div key={`trigger-${triggerId}`} className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 space-y-2">
                          {/* 内容区域 */}
                          <div className="space-y-1.5">
                            {isEditing ? (
                              // 编辑模式
                              <>
                                <input
                                  type="text"
                                  value={editState?.editedTitle || trigger.title}
                                  onChange={(e) => handleTitleChange(triggerId, e.target.value)}
                                  className="w-full text-sm font-medium text-gray-800 leading-tight px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                                  placeholder={t('results.titlePlaceholder')}
                                />
                                <textarea
                                  value={editState?.editedExplanation || trigger.explanation}
                                  onChange={(e) => handleExplanationChange(triggerId, e.target.value)}
                                  rows={3}
                                  className="w-full text-xs text-gray-600 leading-relaxed px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none bg-white"
                                  placeholder={t('results.explanationPlaceholder')}
                                  maxLength={300}
                                />
                              </>
                            ) : (
                              // 显示模式
                              <>
                                <div className="flex items-start justify-between">
                                  <h4 className="text-sm font-medium text-gray-800 leading-tight flex-1 pr-2">
                                {trigger.title}
                              </h4>
                                  {/* 只有非自定义trigger才显示举报按钮 */}
                                  {!trigger.isCustom && (
                                  <button
                                    onClick={() => handleReportTrigger(triggerId)}
                                    className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition-colors"
                                    title={t('report.button')}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                  </button>
                                  )}
                                </div>
                              <p className="text-xs text-gray-600 leading-relaxed">
                                {trigger.explanation}
                              </p>
                              </>
                            )}
                            </div>
                            
                          {/* 操作区域 */}
                          <div className="pt-1.5 border-t border-gray-100">
                            {isEditing ? (
                              // 编辑模式的按钮
                              <div className="flex items-center justify-between">
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleSaveEdit(triggerId)}
                                    disabled={editingTriggers[triggerId]?.isSaving}
                                    className={`px-2.5 py-0.5 rounded text-xs transition-colors ${
                                      editingTriggers[triggerId]?.isSaving 
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                        : 'bg-teal-100 text-teal-700 hover:bg-teal-200'
                                    }`}
                                  >
                                    {editingTriggers[triggerId]?.isSaving 
                                      ? t('complete.saving') 
                                      : t('common.save')
                                    }
                                  </button>
                                  <button
                                    onClick={() => handleCancelEdit(triggerId)}
                                    disabled={editingTriggers[triggerId]?.isSaving}
                                    className={`px-2.5 py-0.5 rounded text-xs transition-colors ${
                                      editingTriggers[triggerId]?.isSaving 
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                  >
                                    {t('common.cancel')}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              // 正常模式的checkbox和编辑按钮
                              <div className="flex items-center justify-between">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input 
                              type="checkbox" 
                                    checked={triggerStates[triggerId]?.isAccepted || false}
                              onChange={(e) => {
                                      handleAcceptanceChange(triggerId, e.target.checked);
                              }}
                              className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 focus:ring-2 bg-white !bg-white dark:!bg-white"
                            />
                            <span className={`text-sm font-medium transition-colors ${
                                    triggerStates[triggerId]?.isAccepted 
                                ? 'text-teal-700' 
                                : 'text-gray-600'
                            }`}>
                                    {t('results.directAccept')}
                            </span>
                          </label>
                                
                                {/* 编辑按钮 - 紧凑版 */}
                                <button
                                  onClick={() => handleEditTrigger(triggerId)}
                                  className="flex items-center space-x-1 px-1.5 py-0.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors text-xs"
                                  title={t('common.edit')}
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  <span>
                                    {trigger.isCustom ? t('common.modify') : t('results.acceptAfterEdit')}
                                  </span>
                                </button>
                        </div>
                            )}
                      </div>
                        </div>
                      );
                    })}

                    {/* 添加自定义trigger卡片 */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 space-y-2">
                      {showAddTriggerForm ? (
                        // 添加trigger表单
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              {t('common.title')}
                            </label>
                            <input
                              type="text"
                              value={newTriggerTitle}
                              onChange={(e) => setNewTriggerTitle(e.target.value)}
                              className="w-full text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                                                              placeholder={t('common.triggerTitlePlaceholder')}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              {t('common.explanation')}
                            </label>
                                                        <textarea
                              value={newTriggerExplanation}
                              onChange={(e) => setNewTriggerExplanation(e.target.value)}
                              rows={3}
                              className="w-full text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none bg-white"
                              placeholder={t('common.explanationPlaceholder')}
                              maxLength={300}
                            />
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={handleAddTrigger}
                              disabled={!newTriggerTitle.trim() || !newTriggerExplanation.trim()}
                              className={`px-3 py-1 rounded text-xs transition-colors ${
                                !newTriggerTitle.trim() || !newTriggerExplanation.trim()
                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                  : 'bg-teal-100 text-teal-700 hover:bg-teal-200'
                              }`}
                            >
                              {t('common.add')}
                            </button>
                            <button
                              onClick={handleCancelAddTrigger}
                              className="px-3 py-1 rounded text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                            >
                              {t('common.cancel')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        // 显示添加按钮
                        <button
                          onClick={() => setShowAddTriggerForm(true)}
                          className="w-full flex items-center justify-center space-x-2 p-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-teal-400 hover:bg-teal-50 transition-colors text-gray-600 hover:text-teal-600"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          <span className="text-sm font-medium">
                            {t('common.addCustomTrigger')}
                          </span>
                        </button>
                      )}
                    </div>

                        {/* 提交按钮 - 重新分析时隐藏 */}
                        {!isReanalyzing && (
                        <div className="pt-4">
                          {(() => {
                            // 计算是否有选中的triggers
                            const hasAcceptedTriggers = triggers.some(trigger => {
                              const state = triggerStates[trigger.id];
                              return state && state.isAccepted;
                            });
                            
                            const isDisabled = isSubmittingTriggers || !hasAcceptedTriggers;
                            
                            return (
                              <>
                  <button
                    onClick={handleRatingSubmit}
                                  disabled={isDisabled}
                            className={`w-full py-3 rounded-lg transition-colors text-sm font-medium flex items-center justify-center ${
                                    isDisabled
                                ? 'bg-gray-400 cursor-not-allowed text-white'
                                : 'bg-teal-600 hover:bg-teal-700 text-white'
                            }`}
                          >
                            {isSubmittingTriggers ? (
                              <>
                                <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                {t('results.submitting')}
                              </>
                            ) : (
                              t('results.submitButton')
                            )}
                  </button>
                                
                                {/* 重新分析提示 */}
                                {!hasAcceptedTriggers && !isSubmittingTriggers && (
                                  <div className="mt-3 text-center">
                                    <p className="text-sm text-gray-800">
                                      {t('results.reanalyzeHint')}
                                      <button
                                        onClick={handleReanalyze}
                                        disabled={isReanalyzing}
                                        className="ml-1 text-teal-600 hover:text-teal-700 underline font-medium inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        {isReanalyzing ? (
                                          <>
                                            <svg className="animate-spin h-3 w-3 mr-1" viewBox="0 0 24 24">
                                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            {t('results.reanalyzing')}
                                          </>
                                        ) : (
                                          <>
                                            {t('results.reanalyze')}
                                            <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                          </>
                                        )}
                                      </button>
                                    </p>
                        </div>
                                )}
                                
                                {/* AI理解能力有限提示 */}
                                {!hasAcceptedTriggers && !isSubmittingTriggers && (
                                  <div className="mt-2 text-center">
                                    <p className="text-sm text-gray-600">
                                      {t('results.aiLimitationHint')}
                                      <button
                                        onClick={handleTryNewImage}
                                        className="ml-1 text-teal-600 hover:text-teal-700 underline font-medium inline-flex items-center"
                                      >
                                        {t('results.tryNewImage')}
                                        <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                      </button>
                                    </p>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                          </div>
                        )}
                      </div>
                </div>
              )}


              {/* 完成状态 */}
              {currentStep === 'complete' && (
                    <div className="space-y-2">
                      {/* 上传的图片预览和用户信息展示 - 居中版 */}
                      <div className="p-4 space-y-2">
                        <h3 className="text-lg font-medium text-gray-800 text-center">
                        {isImageAnonymized ? t('complete.anonymizedImage') : t('complete.uploadedImage')}
                        </h3>
                        
                        {/* 图片预览 - 居中版 */}
                        {imageUrl && (
                          <div className="flex flex-col items-center space-y-3">
                            <div className="relative">
                              <img
                                src={imageUrl}
                                alt="Analysis content"
                                width={120}
                                height={90}
                                onClick={handleImagePreviewClick}
                                className="object-contain cursor-pointer hover:opacity-75 transition-opacity"
                                title={t('analysis.imageUpload.clickToEnlarge') || '点击放大查看'}
                              />
                            </div>
                            
                          </div>
                        )}

                        {/* 用户信息展示 - 居中版 */}
                        {(userIdentity.gender || userIdentity.ageGroup || userIdentity.occupation) && (
                          <div className="flex items-center justify-center gap-2 text-xs text-gray-600">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span>
                              {userIdentity.gender && t(`analysis.basicInfo.gender.${userIdentity.gender}`)}
                              {userIdentity.gender && userIdentity.ageGroup && ' • '}
                              {userIdentity.ageGroup && t(`analysis.basicInfo.age.${userIdentity.ageGroup}`)}
                              {(userIdentity.gender || userIdentity.ageGroup) && userIdentity.occupation && ' • '}
                              {userIdentity.occupation && t(`analysis.basicInfo.occupation.${userIdentity.occupation}`)}
                                </span>
                          </div>
                        )}

                        {/* 数据管理工具 - 简化版 */}
                        
                          <section className="py-0 border-t border-gray-100">
                            <div className="max-w-4xl mx-auto px-1">
                              <div className="p-2">
                                <div className="text-xs text-gray-600 text-center space-y-1">
                                  <p>{t('complete.evaluation.dataManagement.description')}</p>
                                  <p>
                                    {t('complete.evaluation.dataManagement.actionPrefix')}
                                    <Link
                                      to="/dashboard"
                                      className="text-teal-600 hover:text-teal-700 underline font-medium"
                                    >
                                      {t('complete.evaluation.dataManagement.historyRecord')}
                                    </Link>
                                    {t('complete.evaluation.dataManagement.actionSuffix')}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </section>
                      </div>
                    </div>
              )}
            </div>
          </div>
        </section>

        {/* 用户确认的准确结果展示 */}
        {currentStep === 'complete' && confirmedAccurateResults.length > 0 && (
          <section className="py-0">
            <div className="max-w-4xl mx-auto px-1">
              <div className="bg-white p-4">
                <h2 className="text-lg font-semibold mb-3 text-gray-800">
                                          {t('complete.confirmedResults')}
                </h2>
                <div className="space-y-3">
                  {confirmedAccurateResults.map((result) => (
                    <div key={result.id} className="border border-teal-200 rounded-lg p-3 bg-teal-50">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-0.5">
                          <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-teal-800 mb-1">
                            {result.title}
                          </h3>
                          <p className="text-xs text-teal-700 leading-relaxed">
                            {result.explanation}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}


         {/* 缓解焦虑建议模块 */}
        {currentStep === 'complete' && showSuggestions && (
          <section className="py-0" data-section="suggestions">
            <div className="max-w-4xl mx-auto px-1">
              <div className="bg-white p-4">
                  <h2 className="text-xl font-semibold mb-2 text-gray-800">
                    {t('complete.suggestions.title')}
                  </h2>
                  <div className="bg-teal-50 border border-teal-100 rounded-lg p-3 mb-4">
                        <div className="flex items-start space-x-2">
                          <svg className="w-3 h-3 text-teal-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-xs text-teal-700 leading-snug whitespace-pre-line">
                    {t('complete.suggestions.subtitle')}
                  </p>
                      </div>
                  </div>
                 
                  
                  {/* 错误提示 */}
                  {suggestionError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-600 text-sm">{suggestionError}</p>
                    </div>
                  )}

                  {/* 显示所有已生成的建议 */}
                  <div className="space-y-2 mb-4">
                    {(['cognitive_reappraisal', 'problem_focus_coping', 'media_regulation'] as const)
                      .filter(type => suggestions[type])
                      .sort((a, b) => {
                        // 按生成顺序排列
                        const aIndex = suggestionGenerationOrder.indexOf(a);
                        const bIndex = suggestionGenerationOrder.indexOf(b);
                        return aIndex - bIndex;
                      })
                      .map((type) => (
                        <div key={type} className="border border-teal-200 rounded-lg overflow-hidden">
                          {/* 建议标题栏 */}
                          <div className="w-full p-2 bg-teal-50 flex items-center justify-between">
                            <h4 className="font-medium text-teal-800 text-sm truncate flex-1 min-w-0">
                              {t(`complete.suggestions.types.${type}`)}
                            </h4>
                            <button
                              onClick={() => handleReportSuggestion(type)}
                              className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition-colors ml-2"
                              title={t('report.button')}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                              </svg>
                            </button>
                          </div>
                          
                          <div className="p-4 bg-white">
                            <div
                              className={`text-gray-700 text-xs leading-relaxed prose prose-xs prose-gray max-w-none [&>p]:mb-3 [&>p:last-child]:mb-0 ${
                                expandedSuggestions[type] ? '' : 'line-clamp-4'
                              }`}
                            >
                              <ReactMarkdown>{suggestions[type].suggestion}</ReactMarkdown>
                            </div>
                            {suggestions[type].from_cache && (
                              <p className="text-xs text-gray-500 mt-3 italic">{t('complete.suggestions.fromCache')}</p>
                            )}
                            <button
                              type="button"
                              onClick={() => setExpandedSuggestions(prev => ({
                                ...prev,
                                [type]: !prev[type]
                              }))}
                              className="mt-2 inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 font-medium"
                              aria-expanded={Boolean(expandedSuggestions[type])}
                            >
                              {expandedSuggestions[type]
                                ? t('complete.suggestions.collapse')
                                : t('complete.suggestions.expand')}
                              <svg
                                className={`w-3.5 h-3.5 transition-transform ${
                                  expandedSuggestions[type] ? 'rotate-180' : ''
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                              
                            {/* 建议评分区域 - 始终显示 */}
                            <div className="mt-4 pt-3 border-t border-gray-100">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-teal-700">
                                  {t('complete.suggestions.rating.title')} <span className="text-red-500">*</span>
                                </span>
                                <span className="text-xs text-gray-400">
                                  {t('complete.suggestions.rating.scale')}
                                </span>
                              </div>
                              
                              <div className="flex space-x-1">
                                {[1, 2, 3, 4, 5].map((rating) => (
                                  <button
                                    key={rating}
                                    onClick={() => handleSuggestionRatingChange(type, rating)}
                                    className={`flex-1 py-1.5 px-2 rounded text-xs font-medium transition-colors ${
                                      suggestionRatings[type] === rating
                                        ? 'bg-teal-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                  >
                                    {rating}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>

                  {/* 加载状态 */}
                  {Object.values(loadingSuggestions).some(loading => loading) && (
                    <div className="mb-4">
                      <div className="flex flex-col items-center text-sm text-gray-600">
                        {/* 时间信息 */}
                        {Object.keys(currentSuggestionTimes).length > 0 && (
                          <div className="text-xs text-gray-500 text-center mt-2">
                            {language === 'zh' 
                              ? `正在生成建议，已用时${Math.floor(Object.values(currentSuggestionTimes)[0] / 1000)}秒\n预计：10秒`
                              : `Generating suggestions, used ${Math.floor(Object.values(currentSuggestionTimes)[0] / 1000)}s\nExpected: 10s`
                            }
                          </div>
                        )}
                        {/* 显示每个建议的生成状态 */}
                        <div className="space-y-2 text-xs text-gray-500 mt-2">
                          {(['cognitive_reappraisal', 'problem_focus_coping', 'media_regulation'] as const).map(type => (
                            <div key={type} className="flex items-center">
                              <div className={`w-2 h-2 rounded-full mr-2 ${
                                suggestions[type] 
                                  ? 'bg-green-500' 
                                  : loadingSuggestions[type] 
                                    ? 'bg-yellow-500 animate-pulse' 
                                    : 'bg-gray-300'
                              }`} />
                              <span className={suggestions[type] ? 'text-green-600' : loadingSuggestions[type] ? 'text-yellow-600' : 'text-gray-400'}>
                                {t(`complete.suggestions.types.${type}`)}
                                {suggestions[type] && ' ✓'}
                                {loadingSuggestions[type] && ' ...'}
                              </span>
                            </div>
                          ))}
                        </div>
                        
                        
                      </div>
                    </div>
                  )}


               </div>
            </div>
          </section>
        )}

        {/* 评价反馈模块 */}
        {currentStep === 'complete' && showSuggestions && !evaluationSubmitted && Object.keys(suggestions).length > 0 && (
          <section className="py-6">
            <div className="max-w-4xl mx-auto px-1">
              <div className="bg-white p-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-800">
                  {t('complete.evaluation.title')}
                </h2>
                <div className="mb-4 mt-1">
                  <p className={`text-teal-600 text-base ${showEvaluationHeartbeatAnimation ? 'heartbeat-1' : ''}`}>
                  {t('complete.evaluation.subtitle')}
                </p>
                  </div>

              

                <div className="space-y-6">
                  {/* 当前焦虑程度滑动条 */}
                  <div>
                    <label className="block font-medium text-gray-800 mb-2">
                      <span className="text-red-500 mr-1">*</span>
                      {t('complete.evaluation.anxietyLevel.title')}
                    </label>
                   
                    {/* 显示初始值提示 */}
                    <p className="text-xs text-gray-500 mb-2">
                      {language === 'zh' 
                        ? `初始值：${localStorage.getItem('firstAnxietyLevel') || '0'}/10`
                        : `Initial value: ${localStorage.getItem('firstAnxietyLevel') || '0'}/10`
                      }
                    </p>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>{t('complete.evaluation.anxietyLevel.notAnxious')}</span>
                        <span className="font-medium text-gray-700">
                          {evaluationData.emoIntensityAfter}/10
                        </span>
                        <span>{t('complete.evaluation.anxietyLevel.extremelyAnxious')}</span>
                      </div>
                      
                      <div className="relative">
                        <input
                          type="range"
                          min="0"
                          max="10"
                          value={evaluationData.emoIntensityAfter}
                          onChange={(e) => updateEvaluationData(prev => ({ 
                            ...prev, 
                            emoIntensityAfter: parseInt(e.target.value) 
                          }))}
                          className="slider w-full h-2 bg-gradient-to-r from-green-200 via-yellow-200 to-red-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <div className="flex justify-between text-xs text-gray-400 mt-1">
                          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                            <span key={num} className={evaluationData.emoIntensityAfter === num ? 'text-gray-700 font-medium' : ''}>
                              {num}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                     
                    </div>
                    
                    
                  </div>

                  {/* 三个评价问题 */}
                  <div>
                    <h3 className="font-medium text-gray-800 mb-2">
                      {t('complete.evaluation.questions.title')}
                    </h3>
                    <p className="text-xs text-gray-600 mb-2">
                      {t('complete.evaluation.questions.scale')}
                    </p>
                    
                    <div className="space-y-4">
                      {/* 问题1: 触发点准确性 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <span className="text-red-500 mr-1">*</span>
                          {t('complete.evaluation.questions.relatability')}
                        </label>
                        <div className="flex items-center space-x-3">
                          <div className="flex space-x-1">
                          {[1, 2, 3, 4, 5].map((value) => (
                            <button
                              key={value}
                                      onClick={() => updateEvaluationData(prev => ({ 
          ...prev, 
          relatabilityAfter: value 
        }))}
                                className={`w-8 h-8 rounded-full border text-xs font-medium transition-colors ${
                                evaluationData.relatabilityAfter === value
                                  ? 'bg-teal-500 border-teal-500 text-white'
                                  : 'bg-white border-gray-300 text-gray-600 hover:border-teal-300 hover:text-teal-600'
                              }`}
                            >
                              {value}
                            </button>
                          ))}
                          </div>
                          {evaluationData.relatabilityAfter > 0 && (
                            <span className="text-sm text-gray-600 ml-2">
                              {t(`complete.evaluation.questions.ratingLabels.${evaluationData.relatabilityAfter}`)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* 问题2: 建议有帮助性 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <span className="text-red-500 mr-1">*</span>
                          {t('complete.evaluation.questions.helpfulness')}
                        </label>
                        <div className="flex items-center space-x-3">
                          <div className="flex space-x-1">
                            {[1, 2, 3, 4, 5].map((value) => (
                              <button
                                key={value}
                                onClick={() => updateEvaluationData(prev => ({ 
                                  ...prev, 
                                  helpfulnessAfter: value 
                                }))}
                                className={`w-8 h-8 rounded-full border text-xs font-medium transition-colors ${
                                  evaluationData.helpfulnessAfter === value
                                    ? 'bg-teal-500 border-teal-500 text-white'
                                    : 'bg-white border-gray-300 text-gray-600 hover:border-teal-300 hover:text-teal-600'
                                }`}
                              >
                                {value}
                              </button>
                            ))}
                          </div>
                          {evaluationData.helpfulnessAfter > 0 && (
                            <span className="text-sm text-gray-600 ml-2">
                              {t(`complete.evaluation.questions.ratingLabels.${evaluationData.helpfulnessAfter}`)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* 问题3: 学习能力 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <span className="text-red-500 mr-1">*</span>
                          {t('complete.evaluation.questions.learnability')}
                        </label>
                        <div className="flex items-center space-x-3">
                          <div className="flex space-x-1">
                            {[1, 2, 3, 4, 5].map((value) => (
                              <button
                                key={value}
                                onClick={() => updateEvaluationData(prev => ({ 
                                  ...prev, 
                                  learnabilityAfter: value 
                                }))}
                                className={`w-8 h-8 rounded-full border text-xs font-medium transition-colors ${
                                  evaluationData.learnabilityAfter === value
                                    ? 'bg-teal-500 border-teal-500 text-white'
                                    : 'bg-white border-gray-300 text-gray-600 hover:border-teal-300 hover:text-teal-600'
                                }`}
                              >
                                {value}
                              </button>
                            ))}
                          </div>
                          {evaluationData.learnabilityAfter > 0 && (
                            <span className="text-sm text-gray-600 ml-2">
                              {t(`complete.evaluation.questions.ratingLabels.${evaluationData.learnabilityAfter}`)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  

                  {/* 建议类型选择 */}
                  {Object.keys(suggestions).length > 0 && (
                    <div>
                      <label className="block font-medium text-gray-700 mb-2">
                        <span className="text-red-500 mr-1">*</span>
                        {t('complete.evaluation.suggestionChoice.title')}
                      </label>
                     
                      <div className="space-y-3">
                        {Object.keys(suggestions).map((type) => (
                          <label key={type} className="flex items-center p-2 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                            <input
                              type="radio"
                              name="mostEffectiveSuggestion"
                              value={type}
                              checked={evaluationData.mostEffectiveSuggestion === type}
                                      onChange={(e) => updateEvaluationData(prev => ({
          ...prev,
          mostEffectiveSuggestion: e.target.value
        }))}
                              className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500 bg-white !bg-white dark:!bg-white"
                            />
                            <span className="ml-3 text-xs text-gray-700">
                              {t(`complete.suggestions.types.${type}`)}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 文字反馈 */}
                  <div>
                    <label className="block font-medium text-gray-700 mb-2">
                      {t('complete.evaluation.feedback.title')}
                    </label>
                    <textarea
                      value={evaluationData.comment}
                              onChange={(e) => updateEvaluationData(prev => ({
          ...prev,
          comment: e.target.value
        }))}
                      placeholder={t('complete.evaluation.feedback.placeholder')}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none text-xs placeholder:text-s bg-white"
                      rows={4}
                      maxLength={300}
                    />
                  </div>



                  {/* 提交按钮 */}
                  <div className="pt-2">
                  {/* 错误提示 */}
                  {evaluationError && (
                    <div className="mb-4 pt-3">
                      <p className="text-red-600 text-sm">{evaluationError}</p>
                    </div>
                  )}

                  {/* 建议评分提示 */}
                  {!suggestionRatings['cognitive_reappraisal'] && 
                   !suggestionRatings['problem_focus_coping'] && 
                   !suggestionRatings['media_regulation'] && (
                    <div className="mb-3">
                      <p className="text-orange-600 text-sm">{t('complete.evaluation.suggestionRatingHint')}</p>
                    </div>
                  )}

                    <button
                      onClick={handleEvaluationSubmit}
                      disabled={isSubmittingEvaluation || 
                        evaluationData.relatabilityAfter === 0 || 
                        evaluationData.helpfulnessAfter === 0 || 
                        evaluationData.learnabilityAfter === 0 ||
                        !suggestionRatings['cognitive_reappraisal'] ||
                        !suggestionRatings['problem_focus_coping'] ||
                        !suggestionRatings['media_regulation']}
                      className={`w-full px-6 py-3 rounded-lg font-medium transition-colors ${
                        isSubmittingEvaluation || 
                        evaluationData.relatabilityAfter === 0 || 
                        evaluationData.helpfulnessAfter === 0 || 
                        evaluationData.learnabilityAfter === 0 ||
                        !suggestionRatings['cognitive_reappraisal'] ||
                        !suggestionRatings['problem_focus_coping'] ||
                        !suggestionRatings['media_regulation']
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : 'bg-teal-600 text-white hover:bg-teal-700'
                      }`}
                    >
                      {isSubmittingEvaluation ? t('complete.evaluation.submitting') : t('complete.evaluation.submitButton')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

         {/* 评价成功提示 */}
         {evaluationSubmitted && (
          <section className="py-8">
            <div className="max-w-4xl mx-auto">
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <svg className="w-6 h-6 text-teal-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <h3 className="text-lg font-semibold text-teal-800">
                    {t('complete.evaluation.success')}
                  </h3>
                </div>
                <p className="text-teal-700 mb-4">
                  {t('complete.evaluation.thankYou')}
                </p>
                    <button
                      onClick={() => {
                        reset();
                        setAnxietyLevel(0); // 重置焦虑程度
                        setShowSuggestions(false);
                        setSuggestions({});
                        setLoadingSuggestions({});
                        setSuggestionError(null);
                        setExpandedSuggestions({}); // 添加折叠状态清理
                        // 从localStorage读取第一个焦虑程度评分和抽奖联系方式
                        const storedFirstAnxietyLevel = localStorage.getItem('firstAnxietyLevel');
                        const initialEmoIntensityAfter = storedFirstAnxietyLevel ? parseInt(storedFirstAnxietyLevel) : 0;
                        const storedRaffleContactInfo = localStorage.getItem('raffleContactInfo');
                        const initialContactInfo = storedRaffleContactInfo || '';
                        
                        setEvaluationData({
                          emoIntensityAfter: initialEmoIntensityAfter,
                          relatabilityAfter: 0,
                          helpfulnessAfter: 0,
                          learnabilityAfter: 0,
                          comment: '',
                          mostEffectiveSuggestion: '',
                          contactInfo: initialContactInfo
                        });
                        setEvaluationSubmitted(false);
                        setEvaluationError(null);
                        updateInterviewParticipation(false);
                        updateInterviewContactInfo('');
                        setContactSubmitted(false);
                        setContactSubmitting(false);
                        setIsImageAnonymized(false); // 重置匿名化状态
                      }}
                  className="w-full px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
                    >
                            {t('complete.newAnalysisButton')}
                    </button>
                        </div>
                  </div>
          </section>
        )}

        {/* 抽奖活动和访谈参与模块 */}
        {currentStep === 'complete' && showSuggestions && Object.keys(suggestions).length > 0 && (
          <section id="participate-activities-section" className="py-6">
            <div className="max-w-4xl mx-auto px-1">
              <div className="bg-white p-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-800">
                                          {t('complete.participateInActivities')}
                </h2>
                
                <div className="space-y-6">
                  {/* 抽奖活动联系方式 */}
                  <div>
                    <label className="block font-medium text-gray-700 mb-2">
                      {t('complete.evaluation.contactInfo.title')}
                    </label>
                    <p className="text-sm text-teal-600 mb-3">
                      {t('complete.evaluation.contactInfo.subtitle')}
                    </p>
                    {(() => {
                      // 检查是否提交了评价
                      const hasSubmittedEvaluation = evaluationSubmitted;
                      
                                             return (
                         <>
                           <div 
                            className={`relative ${!hasSubmittedEvaluation ? 'cursor-pointer' : ''}`}
                             onClick={() => {
                              if (!hasSubmittedEvaluation) {
                                 setShowContactHint(true);
                               }
                             }}
                           >
                             <input
                               type="text"
                               value={evaluationData.contactInfo}
                               onChange={(e) => {
                                if (hasSubmittedEvaluation) {
                                   setEvaluationData(prev => ({ 
                                     ...prev, 
                                     contactInfo: e.target.value 
                                   }));
                                  // 保存到localStorage
                                  localStorage.setItem('raffleContactInfo', e.target.value);
                                   setShowContactHint(false);
                                 }
                               }}
                               onClick={(e) => {
                                if (!hasSubmittedEvaluation) {
                                   e.preventDefault();
                                   setShowContactHint(true);
                                 }
                               }}
                               onFocus={() => {
                                if (!hasSubmittedEvaluation) {
                                   setShowContactHint(true);
                                 }
                               }}
                               onTouchStart={() => {
                                if (!hasSubmittedEvaluation) {
                                   setShowContactHint(true);
                                 }
                               }}
                               placeholder={t('complete.evaluation.contactInfo.placeholder')}
                              disabled={!hasSubmittedEvaluation}
                               className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-teal-500 transition-colors ${
                                hasSubmittedEvaluation
                                   ? 'border-gray-300 focus:border-teal-500 bg-white'
                                   : 'border-gray-200 bg-gray-50 cursor-not-allowed text-gray-400'
                               }`}
                             />
                           </div>
                          {showContactHint && !hasSubmittedEvaluation && (
                            <div className="mt-2 p-1">
                              <div className="flex items-start space-x-2">
                                <svg className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="text-sm text-gray-500">
                                  {t('complete.evaluation.contactInfo.hint')}
                                </p>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* 访谈参与选项 */}
                  <div className="pt-4 border-t border-gray-100">
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-medium text-gray-800 mb-1">
                          {t('complete.evaluation.interview.title')}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">
                          {t('complete.evaluation.interview.description')}
                        </p>
                        <p className="text-sm text-teal-600 font-medium mb-3">
                          {t('complete.evaluation.interview.reward')}
                        </p>
                        
                        {/* 愿意参与checkbox */}
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={interviewParticipation}
                            onChange={(e) => updateInterviewParticipation(e.target.checked)}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 bg-white !bg-white dark:!bg-white"
                          />
                          <span className="text-sm font-medium text-gray-700">
                            {t('complete.evaluation.interview.willingToParticipate')}
                          </span>
                        </label>
                      </div>

                      {/* 访谈联系方式输入框 */}
                      {interviewParticipation && (
                        <div className="mt-3">
                          <div className="flex items-center space-x-2 mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                            {t('complete.evaluation.interview.contactLabel')}
                          </label>
                            <button
                              type="button"
                              onClick={handleSameAsRaffle}
                              className="text-xs text-teal-600 hover:text-teal-700 underline font-medium"
                            >
                              {t('complete.sameAsAbove')}
                            </button>
                          </div>
                          <input
                            type="text"
                            value={interviewContactInfo}
                            onChange={(e) => updateInterviewContactInfo(e.target.value)}
                            placeholder={t('complete.evaluation.interview.placeholder')}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                          />
                        </div>
                      )}
                    </div>
                  </div>

       

                  {/* 联系方式提交按钮 */}
                  <div className="pt-6 border-t border-gray-100">
                    {contactSubmitted ? (
                      <div className="flex flex-col items-center space-y-3">
                        {/* 已提交提示 */}
                        <div className="flex items-center space-x-2 text-teal-600">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-sm font-medium">
                            {t('complete.contactSubmitted')}
                          </span>
                    </div>

                        {/* 重新提交按钮 */}
                    <button
                          onClick={handleContactSubmit}
                          disabled={contactSubmitting}
                          className={`px-6 py-2 rounded-lg font-medium transition-colors text-sm ${
                            contactSubmitting
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-teal-600 text-white hover:bg-teal-700'
                      }`}
                    >
                          {contactSubmitting ? (
                            <div className="flex items-center">
                              <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              {t('complete.updating')}
                  </div>
                          ) : (
                                                          t('complete.resubmit')
                          )}
                        </button>
                </div>
                    ) : (
                      <div className="flex justify-center">
                    <button
                          onClick={handleContactSubmit}
                          disabled={contactSubmitting}
                          className={`px-8 py-3 rounded-lg font-medium transition-colors ${
                            contactSubmitting
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-teal-600 text-white hover:bg-teal-700'
                          }`}
                        >
                          {contactSubmitting ? (
                            <div className="flex items-center">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              {t('complete.saving')}
                            </div>
                          ) : (
                            hasExistingContactInfo() ? t('complete.updateContactInfo') : t('complete.submitContactInfo')
                          )}
                    </button>
                        </div>
                    )}
                  </div>
                  </div>
                </div>
              </div>
            </section>
        )}

                          {/* GAD-7 焦虑自测模块 */}
        <section className="py-2 px-4 border-t border-gray-100">
          <div className="max-w-4xl mx-auto px-1">
            {/* 提示文字 */}
            {(analyzing || isReanalyzing) && (
              <div className="mb-2">
                <p className={`text-teal-600 text-xs ${showHeartbeatAnimation ? 'heartbeat-1' : ''}`}>
                  {t('gad7.waitingHint')}
                </p>
              </div>
            )}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
              {/* GAD-7 标题栏 */}
              <div
                ref={gad7Ref}
                className={`p-3 cursor-pointer flex items-center justify-between transition-all duration-500 ${
                  (analyzing || isReanalyzing) 
                    ? 'bg-teal-50 hover:bg-teal-100 border-teal-500' 
                    : 'hover:bg-gray-50'
                }`}
                onClick={() => setIsGad7Expanded(!isGad7Expanded)}
              >
                <div>
                  <h3 className="text-sm text-gray-600 mb-1">
                    {t('gad7.title')}
                  </h3>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-400">
                    {isGad7Expanded ? t('gad7.collapse') : t('gad7.expand')}
                  </span>
                  <svg
                    className={`w-4 h-4 text-gray-400 transform transition-transform ${
                      isGad7Expanded ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* GAD-7 表单内容 */}
              {isGad7Expanded && (
                <div className="px-4 pb-4 border-t border-gray-100">
                  <div className="space-y-3 mt-3">
                    {/* GAD-7 固定提示词 */}
                    <div className="bg-gray-50 rounded-lg p-2 mb-3">
                      <p className="text-sm font-medium text-gray-700">
                        {t('gad7.instruction')}
                      </p>
                    </div>
                    
                    {/* GAD-7 当前问题 */}
                    {(() => {
                      const questionKeys = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7'];
                      const currentQuestionKey = questionKeys[currentGad7Question];
                      
                      return (
                        <div className="space-y-3">
                          {/* 问题进度 */}
                          <div className="flex justify-between items-center text-xs text-gray-500">
                            <span>问题 {currentGad7Question + 1} / 7</span>
                          </div>
                          
                          {/* 当前问题 */}
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-gray-700">
                              {t(`gad7.questions.${currentQuestionKey}`)}
                            </p>
                            <div className="space-y-1">
                          {[
                            { value: 0, key: 'never' },
                            { value: 1, key: 'several' },
                            { value: 2, key: 'more' },
                            { value: 3, key: 'nearly' }
                          ].map((option) => (
                            <label
                              key={option.value}
                                  className={`flex items-center space-x-2 py-1 cursor-pointer transition-colors ${
                                    gad7Answers[currentQuestionKey] === option.value
                                      ? 'text-teal-700 font-medium'
                                      : 'text-gray-700 hover:text-gray-900'
                              }`}
                            >
                              <input
                                type="radio"
                                    name={currentQuestionKey}
                                value={option.value}
                                    checked={gad7Answers[currentQuestionKey] === option.value}
                                    onChange={() => handleGad7AnswerChange(currentQuestionKey, option.value)}
                                    className="w-3 h-3 text-teal-600 border-gray-300 focus:ring-teal-500 bg-white !bg-white dark:!bg-white"
                                  />
                                  <span className="text-xs leading-relaxed">
                                {t(`gad7.options.${option.key}`)}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>

                          {/* 导航按钮 */}
                          <div className="flex justify-between pt-2">
                      <button
                              onClick={handleGad7Prev}
                              disabled={currentGad7Question === 0}
                              className={`flex items-center space-x-1 text-xs transition-colors ${
                                currentGad7Question === 0
                                  ? 'text-gray-400 cursor-not-allowed'
                                  : 'text-gray-600 hover:text-gray-800'
                              }`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                              </svg>
                                                              <span>{t('complete.previousQuestion')}</span>
                      </button>
                            
                            {currentGad7Question === 6 ? (
                      <button
                                onClick={handleGad7Complete}
                                disabled={gad7Answers[currentQuestionKey] === undefined || gad7Answers[currentQuestionKey] === null}
                                className={`flex items-center space-x-1 text-xs transition-colors ${
                                  gad7Answers[currentQuestionKey] === undefined || gad7Answers[currentQuestionKey] === null
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-teal-600 hover:text-teal-700'
                                }`}
                              >
                                <span>{t('complete.completeTest')}</span>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                      </button>
                            ) : (
                              <button
                                onClick={handleGad7Next}
                                disabled={gad7Answers[currentQuestionKey] === undefined || gad7Answers[currentQuestionKey] === null}
                                className={`flex items-center space-x-1 text-xs transition-colors ${
                                  gad7Answers[currentQuestionKey] === undefined || gad7Answers[currentQuestionKey] === null
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-teal-600 hover:text-teal-700'
                                }`}
                              >
                                <span>{t('complete.nextQuestion')}</span>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                            )}
                    </div>
                        </div>
                      );
                    })()}



                    {/* GAD-7 结果显示 */}
                    {showGad7Result && gad7Score !== null && (
                      <div className="mt-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-medium text-gray-800">
                              {t('gad7.result.title')}
                            </h4>
                            <span className="text-base font-bold text-teal-600">
                              {gad7Score} / 21
                            </span>
                          </div>
                          
                          <div className="text-xs">
                            <div className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              getGad7Level(gad7Score) === 'mild' ? 'bg-teal-100 text-teal-800' :
                              getGad7Level(gad7Score) === 'light' ? 'bg-yellow-100 text-yellow-800' :
                              getGad7Level(gad7Score) === 'moderate' ? 'bg-orange-100 text-orange-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {t(`gad7.result.${getGad7Level(gad7Score)}`)}
                            </div>
                          </div>

                          <p className="text-xs text-gray-700 leading-relaxed">
                            {t(`gad7.result.recommendation.${getGad7Level(gad7Score)}`)}
                          </p>

                          {/* 中度及以上焦虑的额外建议 */}
                          {(getGad7Level(gad7Score) === 'moderate' || getGad7Level(gad7Score) === 'severe') && (
                            <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                              <div className="flex items-start space-x-2">
                                <svg className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="text-xs text-amber-800 leading-relaxed">
                                  {language === 'zh' 
                                    ? '您可以拨打国家心理援助热线 12356，或前往所在地有资质的心理门诊或精神专科医院进行评估和帮助。'
                                    : 'If you feel that anxiety is significantly affecting your daily life, please consider seeking professional help. You can consult mental health professionals, family doctors, or call psychological support hotlines for assistance.'
                                  }
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    

                    {/* 免责声明 */}
                    <p className="text-xs text-gray-500 mt-4 text-center">
                      {t('gad7.disclaimer')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

            {/* 心理援助热线信息 */}
            <section className="py-0">
              <div className="max-w-4xl mx-auto px-1">
                <div className="text-center">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="space-y-1 text-xs text-gray-600 leading-relaxed">
                      <p>
                        {t('mentalHealthSupport.title')}
                      </p>
                      <p>
                        {t('mentalHealthSupport.hotline')}
                      </p>
                      <p>
                        {t('mentalHealthSupport.description')}
                      </p>
                      <p className="font-medium">
                        {t('mentalHealthSupport.encouragement')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      {/* 举报弹框 */}
      {reportingTrigger && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            {!reportSubmitted ? (
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('report.title')}
                </h3>
                
                <p className="text-sm text-gray-600 mb-6">
                  {t('report.question')}
                </p>
                
                <div className="mb-6">
                  <p className="text-sm font-medium text-gray-800 mb-2">
                    {t('report.reasonTitle')}
                  </p>
                  
                  <div className="space-y-3">
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={reportReasons.negativeEmotion}
                        onChange={(e) => handleReportReasonChange('negativeEmotion', e.target.checked)}
                        className="mt-0.5 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 bg-white !bg-white dark:!bg-white"
                      />
                      <span className="text-sm text-gray-700">
                        {t('report.reasons.negativeEmotion')}
                      </span>
                    </label>
                    
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={reportReasons.misdiagnosis}
                        onChange={(e) => handleReportReasonChange('misdiagnosis', e.target.checked)}
                        className="mt-0.5 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 bg-white !bg-white dark:!bg-white"
                      />
                      <span className="text-sm text-gray-700">
                        {t('report.reasons.misdiagnosis')}
                      </span>
                    </label>
                    
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={reportReasons.other}
                        onChange={(e) => handleReportReasonChange('other', e.target.checked)}
                        className="mt-0.5 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 bg-white !bg-white dark:!bg-white"
                      />
                      <span className="text-sm text-gray-700">
                        {t('report.reasons.other')}
                      </span>
                    </label>
                    
                    {reportReasons.other && (
                      <textarea
                        value={reportOtherReason}
                        onChange={(e) => setReportOtherReason(e.target.value)}
                        placeholder={t('report.otherPlaceholder')}
                        rows={3}
                        className="w-full mt-2 p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 text-sm bg-white"
                        maxLength={300}
                      />
                    )}
                  </div>
                </div>
                
                <div className="flex space-x-3">
                  <button
                    onClick={handleSubmitReport}
                    disabled={reportSubmitting}
                    className={`flex-1 px-4 py-2 rounded text-sm font-medium transition-colors ${
                      reportSubmitting
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                  >
                                                {reportSubmitting ? t('complete.submitting') : t('report.submit')}
                  </button>
                  <button
                    onClick={handleCancelReport}
                    disabled={reportSubmitting}
                    className={`flex-1 px-4 py-2 rounded text-sm font-medium transition-colors ${
                      reportSubmitting
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {t('report.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-6 text-center">
                <div className="mb-4">
                  <svg className="w-12 h-12 mx-auto text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('report.success')}
                </h3>
                
                <p className="text-sm text-gray-600 mb-6">
                  {t('report.thankYou')}
                </p>
                
                <button
                  onClick={handleCancelReport}
                  className="px-6 py-2 bg-teal-600 text-white rounded text-sm font-medium hover:bg-teal-700 transition-colors"
                >
                  {t('common.cancel')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 建议举报弹框 */}
      {reportingSuggestion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            {!suggestionReportSubmitted ? (
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('report.title')}
                </h3>
                
                <p className="text-sm text-gray-600 mb-6">
                  {t('report.question')}
                </p>
                
                <div className="mb-6">
                  <p className="text-sm font-medium text-gray-800 mb-2">
                    {t('report.reasonTitle')}
                  </p>
                  
                  <div className="space-y-3">
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={suggestionReportReasons.moreAnxious}
                        onChange={(e) => handleSuggestionReportReasonChange('moreAnxious', e.target.checked)}
                        className="mt-0.5 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 bg-white !bg-white dark:!bg-white"
                      />
                      <span className="text-sm text-gray-700">
                        {t('report.reasons.moreAnxious')}
                      </span>
                    </label>
                    
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={suggestionReportReasons.preachy}
                        onChange={(e) => handleSuggestionReportReasonChange('preachy', e.target.checked)}
                        className="mt-0.5 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 bg-white !bg-white dark:!bg-white"
                      />
                      <span className="text-sm text-gray-700">
                        {t('report.reasons.preachy')}
                      </span>
                    </label>
                    
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={suggestionReportReasons.inappropriate}
                        onChange={(e) => handleSuggestionReportReasonChange('inappropriate', e.target.checked)}
                        className="mt-0.5 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 bg-white !bg-white dark:!bg-white"
                      />
                      <span className="text-sm text-gray-700">
                        {t('report.reasons.inappropriate')}
                      </span>
                    </label>
                    
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={suggestionReportReasons.other}
                        onChange={(e) => handleSuggestionReportReasonChange('other', e.target.checked)}
                        className="mt-0.5 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 bg-white !bg-white dark:!bg-white"
                      />
                      <span className="text-sm text-gray-700">
                        {t('report.reasons.other')}
                      </span>
                    </label>
                    
                    {suggestionReportReasons.other && (
                      <textarea
                        value={suggestionReportOtherReason}
                        onChange={(e) => setSuggestionReportOtherReason(e.target.value)}
                        placeholder={t('report.otherPlaceholder')}
                        rows={3}
                        className="w-full mt-2 p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 text-sm bg-white"
                        maxLength={300}
                      />
                    )}
                  </div>
                </div>
                
                <div className="flex space-x-3">
                  <button
                    onClick={handleSubmitSuggestionReport}
                    disabled={suggestionReportSubmitting}
                    className={`flex-1 px-4 py-2 rounded text-sm font-medium transition-colors ${
                      suggestionReportSubmitting
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                  >
                    {suggestionReportSubmitting ? t('complete.submitting') : t('report.submit')}
                  </button>
                  <button
                    onClick={handleCancelSuggestionReport}
                    disabled={suggestionReportSubmitting}
                    className={`flex-1 px-4 py-2 rounded text-sm font-medium transition-colors ${
                      suggestionReportSubmitting
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {t('report.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-6 text-center">
                <div className="mb-4">
                  <svg className="w-12 h-12 mx-auto text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('report.success')}
                </h3>
                
                <p className="text-sm text-gray-600 mb-6">
                  {t('report.thankYou')}
                </p>
                
                <button
                  onClick={handleCancelSuggestionReport}
                  className="px-6 py-2 bg-teal-600 text-white rounded text-sm font-medium hover:bg-teal-700 transition-colors"
                >
                  {t('common.cancel')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 图片预览模态框 */}
      {showImagePreview && imageUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-[95vw] max-h-[95vh] flex flex-col">
            {/* 关闭按钮 */}
            <button
              onClick={handleCloseImagePreview}
              className="absolute -top-12 right-0 z-10 bg-black bg-opacity-50 text-white hover:bg-opacity-70 rounded-full p-2 transition-colors"
              title={t('common.close') || '关闭'}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* 图片容器 */}
            <div className="flex items-center justify-center max-w-full max-h-full">
              <img
                src={imageUrl}
                alt="Preview"
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                onClick={handleCloseImagePreview}
              />
            </div>
            
            {/* 图片信息 */}
            <div className="mt-4 text-center">
              <div className="bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg inline-block">
                <p className="text-sm font-medium">
                                          {imageFileName || t('complete.uploadedImageTitle')}
                </p>
                <p className="text-xs opacity-75 mt-1">
                                          {t('complete.clickToExitPreview')}
                </p>
              </div>
            </div>
          </div>
          
          {/* 背景点击关闭 */}
          <div 
            className="absolute inset-0 -z-10" 
            onClick={handleCloseImagePreview}
          ></div>
        </div>
      )}

      {/* 新分析确认对话框 */}
      {showNewAnalysisConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <svg className="w-6 h-6 text-orange-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900">
                                          {t('complete.confirmNewAnalysis')}
                </h3>
              </div>
              
              <p className="text-gray-700 mb-6">
                {language === 'zh' 
                                          ? t('complete.newAnalysisConfirmText') 
                  : 'Do you want to analyze a new post? Current post data will be discarded.'
                }
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={handleConfirmNewAnalysis}
                  className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
                >
                                          {t('complete.startNewAnalysis')}
                </button>
                <button
                  onClick={() => setShowNewAnalysisConfirm(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-sm hover:bg-gray-300 transition-colors"
                >
                                          {t('complete.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 页脚 - 精简版 */}
      <footer className="bg-gray-50 border-t border-gray-100 py-6">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-gray-500">
            © 2025 {t('app.title')} - {t('app.subtitle')}
          </p>
        </div>
      </footer>
    </div>
  )
}